import classnames from 'classnames';
import { withTheme } from 'emotion-theming';
import PropTypes from 'prop-types';
import React from 'react';
import createEmotionStyled from 'create-emotion-styled';
import createEmotion from 'create-emotion';
import moment from 'moment';

var context = typeof global !== 'undefined' ? global : {};

var _createEmotion = createEmotion(context, {
	nonce: btoa('web-ui-components')
}),
    caches = _createEmotion.caches,
    css = _createEmotion.css,
    cx = _createEmotion.cx,
    flush = _createEmotion.flush,
    getRegisteredStyles = _createEmotion.getRegisteredStyles,
    hydrate = _createEmotion.hydrate,
    injectGlobal = _createEmotion.injectGlobal,
    keyframes = _createEmotion.keyframes,
    merge = _createEmotion.merge,
    sheet = _createEmotion.sheet;

var emotion = /*#__PURE__*/Object.freeze({
	__proto__: null,
	caches: caches,
	css: css,
	cx: cx,
	flush: flush,
	getRegisteredStyles: getRegisteredStyles,
	hydrate: hydrate,
	injectGlobal: injectGlobal,
	keyframes: keyframes,
	merge: merge,
	sheet: sheet
});

var styled = createEmotionStyled(emotion, React);

var breakpoints = {
  xs1: '0',
  xs2: '480px',
  sm1: '540px',
  sm2: '640px',
  md1: '768px',
  md2: '960px',
  lg1: '1024px',
  lg2: '1280px',
  lg3: '1328px',
  xl1: '1420px',
  xl2: '1920px'
};

var getMediaQuery = function getMediaQuery(css, breakpoint) {
  return '@media (min-width: ' + breakpoint + ') { ' + css + ' }';
};

var breakpointAndUp = {};

var _loop = function _loop(_breakpoint2) {
  if (breakpoints[_breakpoint2] !== '0') {
    breakpointAndUp[_breakpoint2] = function (css) {
      return getMediaQuery(css, breakpoints[_breakpoint2]);
    };
  }
};

for (var _breakpoint2 in breakpoints) {
  _loop(_breakpoint2);
}

var theme = { baseFontSize: 16 }; //DEBTxCYBG pull this from theme component or global?
var baseSpacing = 8;

var spacingHelper = function spacingHelper(value) {
    var fontMultiple = value * baseSpacing;
    var rems = fontMultiple / theme.baseFontSize;
    return rems + 'rem';
};

var spacing = {
    EIGHTH: spacingHelper(0.125),
    QUARTER: spacingHelper(0.25),
    HALF: spacingHelper(0.5),
    THREE_QUARTERS: spacingHelper(0.75),
    SINGLE: spacingHelper(1),
    ONE_AND_QUARTER: spacingHelper(1.25),
    ONE_AND_HALF: spacingHelper(1.5),
    ONE_AND_THREE_QUARTERS: spacingHelper(1.75),
    DOUBLE: spacingHelper(2),
    TWO_AND_HALF: spacingHelper(2.5),
    TRIPLE: spacingHelper(3),
    THREE_AND_HALF: spacingHelper(3.5),
    QUADRUPLE: spacingHelper(4),
    QUINTUPLE: spacingHelper(5),
    SEXTUPLE: spacingHelper(6),
    SEPTUPLE: spacingHelper(7),
    OCTUPLE: spacingHelper(8),
    NONUPLE: spacingHelper(9),
    DECUPLE: spacingHelper(10)
};

var internalPaddingShell = '\n    overflow: auto;\n    padding-bottom: ' + spacing.SINGLE + '; \n    padding-left: ' + spacing.DOUBLE + '; \n    padding-right: 0; \n    padding-top: ' + spacing.DOUBLE + ';\n    ' + breakpointAndUp.md1('\n        padding-left: ' + spacing.TRIPLE + ';\n        padding-right: ' + spacing.SINGLE + ';\n    ') + '\n';

var defineProperty = function (obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
};

var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

var objectWithoutProperties = function (obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
};

var slicedToArray = function () {
  function sliceIterator(arr, i) {
    var _arr = [];
    var _n = true;
    var _d = false;
    var _e = undefined;

    try {
      for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
        _arr.push(_s.value);

        if (i && _arr.length === i) break;
      }
    } catch (err) {
      _d = true;
      _e = err;
    } finally {
      try {
        if (!_n && _i["return"]) _i["return"]();
      } finally {
        if (_d) throw _e;
      }
    }

    return _arr;
  }

  return function (arr, i) {
    if (Array.isArray(arr)) {
      return arr;
    } else if (Symbol.iterator in Object(arr)) {
      return sliceIterator(arr, i);
    } else {
      throw new TypeError("Invalid attempt to destructure non-iterable instance");
    }
  };
}();

var taggedTemplateLiteral = function (strings, raw) {
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
};

var generalPalette = {
  black: 'hsl(0, 0%, 0%)',
  blue: 'hsl(217,33%,38%)',
  blueLighter: 'hsl(210, 97%, 87%)',
  blueLightest: 'hsl(210, 100%, 95%)',
  green: 'hsl(135, 41%, 48%)',
  greenUpdated: 'hsl(67, 100%, 35%)',
  greenLighter: 'hsl(90, 53%, 70%)',
  greenLightest: 'hsl(90, 61%, 88%)',
  greenLightestUpdated: 'hsl(66, 75%, 75%)',
  greenFaint: 'hsl(135, 41%, 70%)',
  greyDarkest: 'hsl(0, 0%, 28%)',
  greyDarker: 'hsl(0, 0%, 45%)',
  grey: 'hsl(0, 0%, 60%)',
  greyUpdated: 'hsl(0, 0%, 55%)',
  greyLighter: 'hsl(0, 0%, 75%)',
  greyLightest: 'hsl(0, 0%, 85%)',
  greyLightestUpdated: 'hsl(0, 0%, 91%)',
  orange: 'hsl(29, 77%, 56%)',
  orangeUpdated: 'hsl(28, 100%, 54%)',
  orangeLighter: 'hsl(30, 45%, 89%)',
  orangeLightestUpdated: 'hsl(31, 100%, 63%)',
  red: 'hsl(360, 93%, 41%)',
  redUpdated: 'hsl(360, 91%, 46%)',
  redLighter: 'hsl(360, 97%, 77%)',
  redLightest: 'hsl(360, 60%, 96%)',
  redLightestUpdated: 'hsl(10, 94%, 80%)',
  yellowLighter: 'hsl(55, 92%, 69%)',
  yellowLightest: 'hsl(55, 100%, 88%)',
  white: 'hsl(0, 0%, 100%)',
  whiteOff: 'hsl(0, 0%, 95%)',
  pebble: 'hsl(210, 46%, 91%)'
};

var black = generalPalette.black,
    blue = generalPalette.blue,
    blueLighter = generalPalette.blueLighter,
    blueLightest = generalPalette.blueLightest,
    green = generalPalette.green,
    greenUpdated = generalPalette.greenUpdated,
    greenLighter = generalPalette.greenLighter,
    greenFaint = generalPalette.greenFaint,
    greenLightest = generalPalette.greenLightest,
    greenLightestUpdated = generalPalette.greenLightestUpdated,
    greyUpdated = generalPalette.greyUpdated,
    greyDarker = generalPalette.greyDarker,
    greyDarkest = generalPalette.greyDarkest,
    greyLightest = generalPalette.greyLightest,
    greyLightestUpdated = generalPalette.greyLightestUpdated,
    orange = generalPalette.orange,
    orangeLighter = generalPalette.orangeLighter,
    red = generalPalette.red,
    redUpdated = generalPalette.redUpdated,
    redLighter = generalPalette.redLighter,
    redLightest = generalPalette.redLightest,
    redLightestUpdated = generalPalette.redLightestUpdated,
    grey = generalPalette.grey,
    greyLighter = generalPalette.greyLighter,
    yellowLighter = generalPalette.yellowLighter,
    yellowLightest = generalPalette.yellowLightest,
    white = generalPalette.white,
    whiteOff = generalPalette.whiteOff,
    pebble = generalPalette.pebble;


var colours = {
  generalPalette: {
    black: black,
    blue: blue,
    blueLighter: blueLighter,
    blueLightest: blueLightest,
    green: green,
    greenUpdated: greenUpdated,
    greenLighter: greenLighter,
    greenLightest: greenLightest,
    greenLightestUpdated: greenLightestUpdated,
    greenFaint: greenFaint,
    greyDarkest: greyDarkest,
    greyDarker: greyDarker,
    grey: grey,
    greyUpdated: greyUpdated,
    greyLighter: greyLighter,
    greyLightest: greyLightest,
    greyLightestUpdated: greyLightestUpdated,
    orange: orange,
    orangeLighter: orangeLighter,
    pebble: pebble,
    red: red,
    redUpdated: redUpdated,
    redLighter: yellowLightest,
    redLightest: redLightest,
    redLightestUpdated: redLightestUpdated,
    yellowLighter: yellowLighter,
    yellowLightest: yellowLightest,
    white: white,
    whiteOff: whiteOff
  },
  component: {
    background: white,
    backgroundDisabled: greyLightest,
    border: greyLightest,
    borderDisabled: greyDarkest,
    focusOutline: 'hsl(208, 59%, 60%)',
    hover: greyLightest,
    locked: greyDarker,
    modalBackdrop: black,
    shell: whiteOff,
    tableCell: 'hsl(0, 0%, 97%)',
    tableHeader: 'hsl(0, 0%, 94%)',
    navigationButtonDisabled: 'hsl(215, 8%, 58%)',
    frame: {
      active: {
        background: 'repeating-linear-gradient(45deg, hsl(45, 0%, 85%),' + 'hsl(45, 0%, 85%) 28px, hsl(46, 0%, 95%) 28px, hsl(46, 0%, 95%) 56px)',
        content: 'hsl(45, 0%, 85%)',
        contentAlternate: 'hsl(46, 0%, 95%)'
      },
      bespoke: {
        background: 'repeating-linear-gradient(45deg, hsl(302, 44%, 70%), ' + 'hsl(302, 44%, 70%) 28px, hsl(302, 44%, 90%) 28px, hsl(302, 44%, 90%) 56px)',
        content: 'hsl(302, 44%, 70%)',
        contentAlternate: 'hsl(302, 44%, 90%)'
      },
      deprecated: {
        background: 'repeating-linear-gradient(45deg, hsl(45, 100%, 46%), ' + 'hsl(45, 100%, 46%) 28px, hsl(46, 100%, 64%) 28px, hsl(46, 100%, 64%) 56px)',
        content: 'hsl(45, 100%, 46%)',
        contentAlternate: 'hsl(46, 100%, 64%)'
      },
      draft: {
        background: 'repeating-linear-gradient(45deg, ' + blue + ', ' + (blue + ' 28px, hsl(205, 52%, 58%) 28px, hsl(205, 52%, 58%) 56px)'),
        content: blue,
        contentAlternate: 'hsl(205, 52%, 58%)'
      },
      outdated: {
        background: 'repeating-linear-gradient(45deg, hsl(269, 69%, 69%), ' + 'hsl(269, 69%, 69%) 28px, hsl(270, 69%, 79%) 28px, hsl(270, 69%, 79%) 56px)',
        content: 'hsl(269, 69%, 69%)',
        contentAlternate: 'hsl(270, 69%, 79%)'
      }
    }
  },
  telephony: {
    answerCallGradient: 'linear-gradient(hsl(54, 100%, 73%), hsl(49, 99%, 64%));',
    calledGradient: 'linear-gradient(hsl(159, 52%, 67%), hsl(153, 41%, 49%));',
    conferenceCallGradient: 'linear-gradient(hsl(193, 100%, 91%), hsl(196, 96%, 80%));',
    conferenceMaskGradient: 'linear-gradient(hsl(248, 86%, 83%), hsl(260, 94%, 72%));',
    dialingCallGradient: 'linear-gradient(hsl(54, 100%, 73%), hsl(49, 99%, 64%));',
    holdCallGradient: 'linear-gradient(hsl(45, 95%, 75%), hsl(37, 91%, 65%));',
    makeCallGradient: 'linear-gradient(hsl(33, 15%, 86%), hsl(180, 3%, 69%));',
    onCallGradient: 'linear-gradient(hsl(129, 80%, 82%), hsl(127, 67%, 63%));',
    answerCall: 'hsl(89, 91%, 40%)',
    closeCall: 'hsl(353, 98%, 41%)',
    dialCall: 'hsl(89, 91%, 40%)',
    endCall: 'hsl(353, 98%, 41%)',
    holdCall: 'hsl(37, 94%, 49%)',
    resumeCall: 'hsl(89, 91%, 40%)'
  },
  typography: {
    body: greyDarkest,
    secondaryBody: greyDarker,
    supplementaryBody: grey
  },
  utility: {
    error: red,
    errorUpdated: redUpdated,
    errorLighter: redLighter,
    errorLightest: redLightest,
    errorLightestUpdated: redLightestUpdated,
    guidance: blue,
    guidanceLighter: blueLighter,
    guidanceLightest: blueLightest,
    important: red,
    importantLighter: redLighter,
    importantLightest: redLightest,
    indicative: orange,
    information: blue,
    informationUpdated: greyUpdated,
    informationLightest: blueLightest,
    informationLightestUpdated: greyLightestUpdated,
    negative: red,
    positive: green,
    rememberLighter: yellowLighter,
    rememberLightest: yellowLightest,
    success: green,
    successUpdated: greenUpdated,
    successLighter: greenLighter,
    successLightest: greenLightest,
    successLightestUpdated: greenLightestUpdated,
    successDisabled: greenFaint,
    tip: green,
    tipLighter: greenLighter,
    tipLightest: greenLightest,
    warning: orange
  },
  state: {
    active: green,
    archived: greyLightest,
    inactive: red,
    notStarted: greyDarker,
    paused: blue,
    pending: orange
  }
};

/* Font size config: usage example ${fontSize('arran')} */

var fontConfig = {
	lewis: {
		xs1: { fontSize: 37, lineHeight: 1.189 },
		sm1: { fontSize: 37, lineHeight: 1.189 },
		md1: { fontSize: 45, lineHeight: 1.067 },
		lg1: { fontSize: 52, lineHeight: 1.154 },
		xl1: { fontSize: 52, lineHeight: 1.154 }
	},
	skye: {
		xs1: { fontSize: 32, lineHeight: 1.125 },
		sm1: { fontSize: 32, lineHeight: 1.125 },
		md1: { fontSize: 40, lineHeight: 1.1 },
		lg1: { fontSize: 45, lineHeight: 1.156 },
		xl1: { fontSize: 45, lineHeight: 1.156 }
	},
	shetland: {
		xs1: { fontSize: 29, lineHeight: 1.241 },
		sm1: { fontSize: 29, lineHeight: 1.241 },
		md1: { fontSize: 34, lineHeight: 1.176 },
		lg1: { fontSize: 39, lineHeight: 1.128 },
		xl1: { fontSize: 39, lineHeight: 1.128 }
	},
	mull: {
		xs1: { fontSize: 26, lineHeight: 1.231 },
		sm1: { fontSize: 26, lineHeight: 1.231 },
		md1: { fontSize: 29, lineHeight: 1.241 },
		lg1: { fontSize: 33, lineHeight: 1.212 },
		xl1: { fontSize: 33, lineHeight: 1.212 }
	},
	islay: {
		xs1: { fontSize: 23, lineHeight: 1.217 },
		sm1: { fontSize: 23, lineHeight: 1.217 },
		md1: { fontSize: 25, lineHeight: 1.28 },
		lg1: { fontSize: 28, lineHeight: 1.286 },
		xl1: { fontSize: 28, lineHeight: 1.286 }
	},
	orkney: {
		xs1: { fontSize: 20, lineHeight: 1.4 },
		sm1: { fontSize: 20, lineHeight: 1.4 },
		md1: { fontSize: 22, lineHeight: 1.273 },
		lg1: { fontSize: 25, lineHeight: 1.28 },
		xl1: { fontSize: 25, lineHeight: 1.28 }
	},
	arran: {
		xs1: { fontSize: 18, lineHeight: 1.333 },
		sm1: { fontSize: 18, lineHeight: 1.333 },
		md1: { fontSize: 19, lineHeight: 1.474 },
		lg1: { fontSize: 21, lineHeight: 1.333 },
		xl1: { fontSize: 21, lineHeight: 1.333 }
	},
	jura: {
		xs1: { fontSize: 16, lineHeight: 1.5 },
		sm1: { fontSize: 16, lineHeight: 1.5 },
		md1: { fontSize: 16, lineHeight: 1.5 },
		lg1: { fontSize: 18, lineHeight: 1.556 },
		xl1: { fontSize: 18, lineHeight: 1.556 }
	},
	uist: {
		xs1: { fontSize: 14, lineHeight: 1.429 },
		sm1: { fontSize: 14, lineHeight: 1.429 },
		md1: { fontSize: 14, lineHeight: 1.429 },
		lg1: { fontSize: 16, lineHeight: 1.5 },
		xl1: { fontSize: 16, lineHeight: 1.5 }
	},
	bute: {
		xs1: { fontSize: 12, lineHeight: 1.333 },
		sm1: { fontSize: 12, lineHeight: 1.333 },
		md1: { fontSize: 13, lineHeight: 1.538 },
		lg1: { fontSize: 13, lineHeight: 1.538 },
		xl1: { fontSize: 13, lineHeight: 1.538 }
	},
	cumbrae: {
		xs1: { fontSize: 11, lineHeight: 1.455 },
		sm1: { fontSize: 11, lineHeight: 1.455 },
		md1: { fontSize: 12, lineHeight: 1.333 },
		lg1: { fontSize: 12, lineHeight: 1.333 },
		xl1: { fontSize: 12, lineHeight: 1.333 }
	}
};

var generateSASS = function generateSASS(mqBreakpoint, size, lineHeight) {
	var styles = '';
	if (parseInt(mqBreakpoint) === 0) {
		styles = 'font-size: ' + size + 'px; line-height: ' + lineHeight + ';';
	} else {
		styles = '\n      @media (min-width: ' + mqBreakpoint + ') {\n        font-size: ' + size + 'px;\n        line-height: ' + lineHeight + ';\n      }\n    ';
	}
	return styles;
};

var fontSizes = {};

for (var fontSize in fontConfig) {
	if (fontConfig.hasOwnProperty(fontSize)) {
		var style = '';
		for (var breakpoint in fontConfig[fontSize]) {
			if (fontConfig[fontSize].hasOwnProperty(breakpoint)) {
				var _fontConfig$fontSize$ = fontConfig[fontSize][breakpoint],
				    size = _fontConfig$fontSize$.fontSize,
				    lineHeight = _fontConfig$fontSize$.lineHeight;

				style += generateSASS(breakpoints[breakpoint], size, lineHeight);
			}
		}
		fontSizes[fontSize] = style;
	}
}

var useFontSize = {

	of: {
		lewis: function lewis() {
			return fontSizes['lewis'];
		},
		skye: function skye() {
			return fontSizes['skye'];
		},
		shetland: function shetland() {
			return fontSizes['shetland'];
		},
		mull: function mull() {
			return fontSizes['mull'];
		},
		islay: function islay() {
			return fontSizes['islay'];
		},
		orkney: function orkney() {
			return fontSizes['orkney'];
		},
		arran: function arran() {
			return fontSizes['arran'];
		},
		jura: function jura() {
			return fontSizes['jura'];
		},
		uist: function uist() {
			return fontSizes['uist'];
		},
		bute: function bute() {
			return fontSizes['bute'];
		},
		cumbrae: function cumbrae() {
			return fontSizes['cumbrae'];
		}
	},

	biggerThan: {
		lewis: function lewis() {
			throw new Error("lewis is the biggest font");
		},
		skye: function skye() {
			return fontSizes['lewis'];
		},
		shetland: function shetland() {
			return fontSizes['skye'];
		},
		mull: function mull() {
			return fontSizes['shetland'];
		},
		islay: function islay() {
			return fontSizes['mull'];
		},
		orkney: function orkney() {
			return fontSizes['islay'];
		},
		arran: function arran() {
			return fontSizes['orkney'];
		},
		jura: function jura() {
			return fontSizes['arran'];
		},
		uist: function uist() {
			return fontSizes['jura'];
		},
		bute: function bute() {
			return fontSizes['uist'];
		},
		cumbrae: function cumbrae() {
			return fontSizes['bute'];
		}
	},

	smallerThan: {
		lewis: function lewis() {
			return fontSizes['skye'];
		},
		skye: function skye() {
			return fontSizes['shetland'];
		},
		shetland: function shetland() {
			return fontSizes['mull'];
		},
		mull: function mull() {
			return fontSizes['islay'];
		},
		islay: function islay() {
			return fontSizes['orkney'];
		},
		orkney: function orkney() {
			return fontSizes['arran'];
		},
		arran: function arran() {
			return fontSizes['jura'];
		},
		jura: function jura() {
			return fontSizes['uist'];
		},
		uist: function uist() {
			return fontSizes['bute'];
		},
		bute: function bute() {
			return fontSizes['cumbrae'];
		},
		cumbrae: function cumbrae() {
			throw new Error("cumbrae is the smallest font");
		}
	}
};

var regexes = {
	alphanumericOrSpace: /^[A-Z0-9\s]*$/,
	bfpo: /^BF\d ?\d[A-Z]{2}|BFPO ?\d{1,4}$/,
	legacyEmail: /^[_a-zA-Z0-9-]+(\.[_a-zA-Z0-9-]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*(\.[a-zA-Z]{2,})$/,
	newEmail: /^[!#%$&'*+/=_a-z0-9-]+(\.[!#%$&'*+/=_a-z0-9-]+)*@[a-z0-9-]+(\.[a-z0-9-]+)*(\.[a-z]{2,})$/i,
	moneyFormatter: /\B(?=(\d{3})+(?!\d))/g,
	postcode: /^(([A-Z]{1,2}\d[A-Z\d]?|ASCN|STHL|TDCU|BBND|[BFS]IQQ|PCRN|TKCA) ?\d[A-Z]{2}|(KY\d|MSR|VG|AI)[ -]?\d{4}|[A-Z]{2} ?\d{2}|GE ?CX)$/,
	postcodeAlt: /^([a-zA-Z]){1}([0-9][0-9]|[0-9]|[a-zA-Z][0-9][a-zA-Z]|[a-zA-Z][0-9][0-9]|[a-zA-Z][0-9]){1}([ ]?)([0-9][a-zA-z][a-zA-z]){1}$/,
	simplifiedAlpha: /([a-z])+/i,
	simplifiedNumeric: /([0-9])+/,
	specialCharacters: /([@!#%&'*+-/=?^_`{|}~])+/i
};

var moneyFormatter = regexes.moneyFormatter;


function lengthCalc(value, targetLength) {
	var newValue = value;
	var i = void 0;

	if (value.length < targetLength) {
		for (i = 0; i < targetLength - value.length; i += 1) {
			newValue = '0'.concat(newValue);
		}
	}
	return newValue;
}

var ACCOUNT_NUMBER_TARGET_LENGTH = 8;
var accountNumberFormat = function accountNumberFormat(value) {
	return '' + lengthCalc(value.toString(), ACCOUNT_NUMBER_TARGET_LENGTH);
};

var CARD_NUMBER_TARGET_LENGTH = 16;
var cardNumberFormat = function cardNumberFormat(value) {
	return '' + lengthCalc(value.toString(), CARD_NUMBER_TARGET_LENGTH).replace(/\B(?=(\d{4})+(?!\d))/g, ' ');
};

var PENCE_DIVIDER = 100;
var penceCurrencyFormat = function penceCurrencyFormat(value, removePrefix) {
	return '' + (Math.sign(value) === -1 ? '-' : '') + (!removePrefix ? '£' : '') + (Math.abs(value) / PENCE_DIVIDER).toFixed(2).toString().replace(moneyFormatter, ',');
};

var poundCurrencyFormat = function poundCurrencyFormat(value) {
	return '\xA3' + value.toString().replace(moneyFormatter, ',');
};

var PRODUCT_NUMBER_TARGET_LENGTH = 3;
var productNumberFormat = function productNumberFormat(value) {
	return '' + lengthCalc(value.toString(), PRODUCT_NUMBER_TARGET_LENGTH);
};

var SORT_CODE_TARGET_LENGTH = 6;
var sortCodeFormat = function sortCodeFormat(value) {
	return '' + lengthCalc(value.toString(), SORT_CODE_TARGET_LENGTH).replace(/\B(?=(\d{2})+(?!\d))/g, '-');
};

var BankingUtils = {
	accountNumberFormat: accountNumberFormat,
	cardNumberFormat: cardNumberFormat,
	lengthCalc: lengthCalc,
	penceCurrencyFormat: penceCurrencyFormat,
	poundCurrencyFormat: poundCurrencyFormat,
	productNumberFormat: productNumberFormat,
	sortCodeFormat: sortCodeFormat
};

var _templateObject = taggedTemplateLiteral(['\n\tdisplay: inline-flex;\n'], ['\n\tdisplay: inline-flex;\n']),
    _templateObject2 = taggedTemplateLiteral(['\n\tpadding-right: ', ';\n\twhite-space: nowrap;\n'], ['\n\tpadding-right: ', ';\n\twhite-space: nowrap;\n']);

var StyledDetails = styled('div')(_templateObject);

var StyledSortCode = styled('div')(_templateObject2, spacing.SINGLE);

var SortCodeAndAccountNumber = function SortCodeAndAccountNumber(_ref) {
	var accountNumber = _ref.accountNumber,
	    sortCode = _ref.sortCode;

	var accountNumberString = typeof accountNumber === 'number' ? BankingUtils.accountNumberFormat(accountNumber) : accountNumber;
	var sortCodeString = typeof sortCode === 'number' ? BankingUtils.sortCodeFormat(sortCode) : sortCode;

	var sortcodeLabel = React.createElement(
		StyledSortCode,
		{ 'aria-label': 'Sortcode: ' + sortCodeString },
		React.createElement(
			'span',
			null,
			sortCodeString
		)
	);

	var accountNumberLabel = React.createElement(
		'div',
		{ 'aria-label': 'Account Number: ' + accountNumberString },
		React.createElement(
			'span',
			null,
			accountNumberString
		)
	);

	return React.createElement(
		StyledDetails,
		null,
		sortcodeLabel,
		accountNumberLabel
	);
};

SortCodeAndAccountNumber.propTypes = {
	/** Account number */
	accountNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	/** Account sort code */
	sortCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

SortCodeAndAccountNumber.defaultProps = {};

var _templateObject$1 = taggedTemplateLiteral(['\n\tmargin-top: ', ';\n'], ['\n\tmargin-top: ', ';\n']),
    _templateObject2$1 = taggedTemplateLiteral(['\n\t', ' white-space: nowrap;\n'], ['\n\t', ' white-space: nowrap;\n']);

var paddingRight = function paddingRight() {
	return 'padding-right: ' + spacing.SINGLE + ';';
};

var StyledAccountAvailable = styled('div')(_templateObject$1, spacing.SINGLE);

var StyledAccountCode = styled('div')(_templateObject2$1, function (props) {
	return props.accountCode && paddingRight();
});

var getAccountCardTitle = function getAccountCardTitle(id, accountNameFont, title) {
	return title && [React.createElement(
		'label',
		{ htmlFor: id, key: 'c-m-account-card__label', className: 'u-faux-block-link__overlay' },
		accountNameFont(title)
	)];
};

var getAccountCardInput = function getAccountCardInput(id, checked, _onChange, _onClick, inputType) {
	return React.createElement('input', {
		id: id,
		key: 'c-m-account-card__input',
		type: inputType,
		checked: checked,
		onChange: function onChange() {
			return _onChange(checked, id);
		},
		onClick: function onClick() {
			return _onClick(id);
		}
	});
};

var getAccountCode = function getAccountCode(accountCode, accountDetailsFont) {
	return accountCode && React.createElement(
		StyledAccountCode,
		{ accountCode: accountCode },
		React.createElement(
			'div',
			{ className: 'u-visually-hidden' },
			'Account code'
		),
		accountDetailsFont(accountCode)
	);
};

var getSortCodeAndAccountNumber = function getSortCodeAndAccountNumber(sortCode, accountNumber, accountDetailsFont) {
	return (sortCode || accountNumber) && accountDetailsFont(React.createElement(SortCodeAndAccountNumber, { accountNumber: accountNumber, sortCode: sortCode }));
};

var getAccountBody = function getAccountBody(accountBody) {
	return accountBody && React.createElement(
		'div',
		{ className: 'c-m-account-card__body', key: 'c-m-account-card__body' },
		React.createElement(
			'div',
			{ className: 'u-rich-text' },
			accountBody.map(function (line) {
				return React.createElement(
					'p',
					null,
					Array.isArray(line) ? line.map(function (element) {
						return React.createElement(
							'span',
							{ className: 'u-display-inline-block' },
							element
						);
					}) : line
				);
			})
		)
	);
};

var getAccountCardBalanceTotal = function getAccountCardBalanceTotal(balanceTotal, accountBalanceFont, balanceAvailable, accountAvailableFont) {
	return balanceTotal && React.createElement(
		'div',
		{ className: 'c-m-account-card__footer', key: 'c-m-account-card__footer' },
		React.createElement(
			'div',
			{ className: 'c-m-account-card__amounts' },
			React.createElement(
				'div',
				{ className: 'c-m-account-card__balance-total' },
				accountBalanceFont(balanceTotal)
			),
			balanceAvailable && React.createElement(
				StyledAccountAvailable,
				null,
				accountAvailableFont(balanceAvailable)
			)
		)
	);
};

var getAccountCardControl = function getAccountCardControl(control, checked) {
	return control && checked && React.createElement(
		'div',
		{ className: 'c-m-account-card__control' },
		control
	);
};

var AccountCard = function AccountCard(_ref) {
	var accountCode = _ref.accountCode,
	    accountNumber = _ref.accountNumber,
	    balanceAvailable = _ref.balanceAvailable,
	    balanceTotal = _ref.balanceTotal,
	    branded = _ref.branded,
	    checked = _ref.checked,
	    className = _ref.className,
	    control = _ref.control,
	    id = _ref.id,
	    interactive = _ref.interactive,
	    link = _ref.link,
	    onChange = _ref.onChange,
	    onClick = _ref.onClick,
	    sortCode = _ref.sortCode,
	    title = _ref.title,
	    type = _ref.type,
	    accountBody = _ref.accountBody,
	    theme = _ref.theme,
	    rest = objectWithoutProperties(_ref, ['accountCode', 'accountNumber', 'balanceAvailable', 'balanceTotal', 'branded', 'checked', 'className', 'control', 'id', 'interactive', 'link', 'onChange', 'onClick', 'sortCode', 'title', 'type', 'accountBody', 'theme']);

	var accountAvailableFont = theme.components.accountCard.fonts.accountAvailable;
	var accountBalanceFont = theme.components.accountCard.fonts.accountBalance;
	var accountDetailsFont = theme.components.accountCard.fonts.accountDetails;
	var accountNameFont = theme.components.accountCard.fonts.accountName;
	var accountNameAnchor = function accountNameAnchor(children) {
		return React.createElement(
			'a',
			{ href: link, className: 'u-faux-block-link__overlay' },
			accountNameFont(children)
		);
	};

	return React.createElement(
		'div',
		_extends({
			className: classnames('c-m-account-card', {
				'c-m-account-card--branded': branded,
				'c-m-account-card--interactive': interactive,
				'is-checked': checked
			}, className)
		}, rest),
		React.createElement(
			'div',
			{
				className: classnames('c-m-account-card__content', {
					'u-faux-block-link': type
				})
			},
			React.createElement(
				'div',
				{ className: 'c-m-account-card__header' },
				React.createElement(
					'div',
					{ className: 'c-m-account-card__meta' },
					React.createElement(
						'h2',
						{ className: 'c-m-account-card__title' },
						!type && title && React.createElement(
							'span',
							null,
							title
						),
						type === 'link' && title && accountNameAnchor(title),
						type === 'radio' && [getAccountCardInput(id, checked, onChange, onClick, 'radio'), getAccountCardTitle(id, accountNameFont, title)],
						type === 'checkbox' && [getAccountCardInput(id, checked, onChange, onClick, 'checkbox'), getAccountCardTitle(id, accountNameFont, title)]
					),
					React.createElement(
						'div',
						{ className: 'c-m-account-card__account-details' },
						getAccountCode(accountCode, accountDetailsFont),
						getSortCodeAndAccountNumber(sortCode, accountNumber, accountDetailsFont)
					)
				),
				getAccountCardControl(control, checked)
			),
			getAccountBody(accountBody),
			getAccountCardBalanceTotal(balanceTotal, accountBalanceFont, balanceAvailable, accountAvailableFont)
		)
	);
};

AccountCard.propTypes = {
	accountCode: PropTypes.string,
	accountNumber: PropTypes.string,
	balanceAvailable: PropTypes.string,
	balanceTotal: PropTypes.string,
	branded: PropTypes.bool,
	checked: PropTypes.bool,
	className: PropTypes.string,
	control: PropTypes.node,
	id: PropTypes.string,
	interactive: PropTypes.bool,
	link: PropTypes.string,
	onChange: PropTypes.func,
	onClick: PropTypes.func,
	sortCode: PropTypes.string,
	title: PropTypes.string,
	accountBody: PropTypes.arrayOf(PropTypes.array),
	type: PropTypes.oneOf(['radio', 'checkbox', 'link'])
};

AccountCard.defaultProps = {
	branded: false,
	checked: false,
	interactive: false,
	onChange: function onChange() {}
};

var WrappedAccountCard = withTheme(AccountCard);

var getThemeIcon = function getThemeIcon(vmIcon, generalIcon) {
  return function (themeName) {
    return themeName === 'vm' ? vmIcon : generalIcon;
  };
};

var _extends$1 = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }return target;
};

function _objectWithoutProperties(obj, keys) {
  var target = {};for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;target[i] = obj[i];
  }return target;
}
var GenericIcon = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties(_ref, ["styles"]);

  return React.createElement("svg", _extends$1({ width: "96", height: "96", viewBox: "0 0 96 96", xmlns: "http://www.w3.org/2000/svg" }, props), React.createElement("path", { d: "M62.862 48L22.508 8.587a4.972 4.972 0 0 1 0-7.114l.148-.144a5 5 0 0 1 6.988 0l43.848 42.826A4.96 4.96 0 0 1 74.982 48a4.959 4.959 0 0 1-1.49 3.845L29.644 94.671a5 5 0 0 1-6.988 0l-.148-.144a4.972 4.972 0 0 1 0-7.114L62.862 48z" }));
});

var _extends$2 = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }return target;
};

function _objectWithoutProperties$1(obj, keys) {
  var target = {};for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;target[i] = obj[i];
  }return target;
}
var VMIcon = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$1(_ref, ["styles"]);

  return React.createElement("svg", _extends$2({ height: "96", viewBox: "0 0 96 96", width: "96", xmlns: "http://www.w3.org/2000/svg" }, props), React.createElement("path", { d: "M66.746 48.048a4.756 4.756 0 0 0-1.398-3.373L36.722 16.048l-6.747 6.746 25.253 25.254-25.253 25.254 6.747 6.746L65.348 51.42a4.756 4.756 0 0 0 1.398-3.373z" }));
});

var getChevronRightSolidSmall = getThemeIcon(VMIcon, GenericIcon);

var _templateObject$2 = taggedTemplateLiteral(['\n\twidth: 100%;\n\tdisplay: flex;\n\tflex-wrap: ', ';\n\talign-items: ', ';\n\tjustify-content: center;\n\n\t', ';\n'], ['\n\twidth: 100%;\n\tdisplay: flex;\n\tflex-wrap: ', ';\n\talign-items: ', ';\n\tjustify-content: center;\n\n\t', ';\n']),
    _templateObject2$2 = taggedTemplateLiteral(['\n\tdisplay: flex;\n\talign-self: stretch;\n\talign-items: ', ';\n\n\t', ';\n'], ['\n\tdisplay: flex;\n\talign-self: stretch;\n\talign-items: ', ';\n\n\t', ';\n']),
    _templateObject3 = taggedTemplateLiteral(['\n\tdisplay: flex;\n\tflex-wrap: ', ';\n\talign-items: ', ';\n\tjustify-content: space-between;\n\twidth: 100%;\n\n\t', ';\n'], ['\n\tdisplay: flex;\n\tflex-wrap: ', ';\n\talign-items: ', ';\n\tjustify-content: space-between;\n\twidth: 100%;\n\n\t', ';\n']),
    _templateObject4 = taggedTemplateLiteral(['\n\tdisplay: flex;\n\tflex-wrap: ', ';\n\talign-items: ', ';\n\twidth: 100%;\n\n\t', ';\n'], ['\n\tdisplay: flex;\n\tflex-wrap: ', ';\n\talign-items: ', ';\n\twidth: 100%;\n\n\t', ';\n']),
    _templateObject5 = taggedTemplateLiteral(['\n\tdisplay: flex;\n\tflex-wrap: ', ';\n\talign-items: ', ';\n\tjustify-content: flex-end;\n\twidth: 100%;\n\n\t', ';\n'], ['\n\tdisplay: flex;\n\tflex-wrap: ', ';\n\talign-items: ', ';\n\tjustify-content: flex-end;\n\twidth: 100%;\n\n\t', ';\n']),
    _templateObject6 = taggedTemplateLiteral(['\n\tdisplay: flex;\n\tflex-wrap: ', ';\n\talign-items: ', ';\n\tjustify-content: flex-start;\n\twidth: 100%;\n\n\t', ';\n'], ['\n\tdisplay: flex;\n\tflex-wrap: ', ';\n\talign-items: ', ';\n\tjustify-content: flex-start;\n\twidth: 100%;\n\n\t', ';\n']),
    _templateObject7 = taggedTemplateLiteral(['\n\tdisplay: flex;\n\twidth: 100%;\n\n\t> div {\n\t\tdisplay: flex;\n\t\twidth: 100%;\n\t}\n\n\t@media (min-width: ', ') {\n\t\tflex-basis: 33.33%;\n\t}\n\n\t', ';\n'], ['\n\tdisplay: flex;\n\twidth: 100%;\n\n\t> div {\n\t\tdisplay: flex;\n\t\twidth: 100%;\n\t}\n\n\t@media (min-width: ', ') {\n\t\tflex-basis: 33.33%;\n\t}\n\n\t', ';\n']);

var baseProps = {
	nodes: PropTypes.arrayOf(PropTypes.node).isRequired,
	isNonWrapping: PropTypes.bool,
	verticalAlignment: PropTypes.string,
	isNonFlexForPrint: PropTypes.bool,
	breakpointForNonStacked: PropTypes.oneOf(['480', '540', '640', '960', '1280'])
};

var defaultProps = {
	isNonWrapping: false,
	isNonFlexForPrint: false,
	verticalAlignment: 'stretch'
};

var CenteredWrapper = styled('div')(_templateObject$2, function (props) {
	return props.isNonWrapping ? 'nowrap' : 'wrap';
}, function (props) {
	return props.alignItems;
}, function (props) {
	return props.breakpointForNonStacked && '@media (max-width: ' + props.breakpointForNonStacked + 'px) {\n\t\t\tdisplay: block;\n\t\t}';
});

var verticalAlignmentToAlignItems = function verticalAlignmentToAlignItems(verticalAlignment) {
	switch (verticalAlignment) {
		case 'top':
			return 'flex-start';
		case 'bottom':
			return 'flex-end';
		case 'center':
			return 'center';
		case 'baseline':
			return 'baseline';
		case 'stretch':
		default:
			return 'stretch';
	}
};

var Centered = function Centered(_ref) {
	var nodes = _ref.nodes,
	    isNonWrapping = _ref.isNonWrapping,
	    verticalAlignment = _ref.verticalAlignment,
	    breakpointForNonStacked = _ref.breakpointForNonStacked;
	return React.createElement(
		CenteredWrapper,
		{
			alignItems: verticalAlignmentToAlignItems(verticalAlignment),
			isNonWrapping: isNonWrapping,
			breakpointForNonStacked: breakpointForNonStacked
		},
		nodes.map(function (x, index) {
			return React.createElement(
				'div',
				{ key: index },
				x
			);
		})
	);
};

Centered.propTypes = _extends({}, baseProps);

Centered.defaultProps = _extends({}, defaultProps);

var JustifiedNodeWrapper = styled('div')(_templateObject2$2, function (props) {
	return props.alignItems;
}, function (props) {
	return props.breakpointForNonStacked && '@media (max-width: ' + props.breakpointForNonStacked + 'px) {\n\t\t\tdisplay: block;\n\t\t}';
});

var JustifiedWrapper = styled('div')(_templateObject3, function (props) {
	return props.isNonWrapping ? 'nowrap' : 'wrap';
}, function (props) {
	return props.alignItems;
}, function (props) {
	return props.breakpointForNonStacked && '@media (max-width: ' + props.breakpointForNonStacked + 'px) {\n\t\t\tdisplay: block;\n\t\t}';
});

var Justified = function Justified(_ref2) {
	var nodes = _ref2.nodes,
	    isNonWrapping = _ref2.isNonWrapping,
	    verticalAlignment = _ref2.verticalAlignment,
	    breakpointForNonStacked = _ref2.breakpointForNonStacked;
	return React.createElement(
		JustifiedWrapper,
		{ isNonWrapping: isNonWrapping, breakpointForNonStacked: breakpointForNonStacked },
		nodes.map(function (x, index) {
			return React.createElement(
				JustifiedNodeWrapper,
				{
					alignItems: verticalAlignmentToAlignItems(verticalAlignment),
					breakpointForNonStacked: breakpointForNonStacked,
					key: index
				},
				x
			);
		})
	);
};

Justified.propTypes = _extends({}, baseProps);

Justified.defaultProps = _extends({}, defaultProps);

var LeftAlignedWrapper = styled('div')(_templateObject4, function (props) {
	return props.isNonWrapping ? 'nowrap' : 'wrap';
}, function (props) {
	return props.alignItems;
}, function (props) {
	return props.breakpointForNonStacked && '@media (max-width: ' + props.breakpointForNonStacked + 'px) {\n\t\t\tdisplay: block;\n\t\t}';
});

var LeftAligned = function LeftAligned(_ref3) {
	var nodes = _ref3.nodes,
	    isNonWrapping = _ref3.isNonWrapping,
	    verticalAlignment = _ref3.verticalAlignment,
	    breakpointForNonStacked = _ref3.breakpointForNonStacked;
	return React.createElement(
		LeftAlignedWrapper,
		{
			alignItems: verticalAlignmentToAlignItems(verticalAlignment),
			isNonWrapping: isNonWrapping,
			breakpointForNonStacked: breakpointForNonStacked
		},
		nodes.map(function (x, index) {
			return React.createElement(
				'div',
				{ key: index },
				x
			);
		})
	);
};

LeftAligned.propTypes = _extends({}, baseProps);

LeftAligned.defaultProps = _extends({}, defaultProps);

var RightAlignedWrapper = styled('div')(_templateObject5, function (props) {
	return props.isNonWrapping ? 'nowrap' : 'wrap';
}, function (props) {
	return props.alignItems;
}, function (props) {
	return props.breakpointForNonStacked && '@media (max-width: ' + props.breakpointForNonStacked + 'px) {\n\t\t\tdisplay: block;\n\t\t}';
});

var RightAligned = function RightAligned(_ref4) {
	var nodes = _ref4.nodes,
	    isNonWrapping = _ref4.isNonWrapping,
	    verticalAlignment = _ref4.verticalAlignment,
	    breakpointForNonStacked = _ref4.breakpointForNonStacked;
	return React.createElement(
		RightAlignedWrapper,
		{
			alignItems: verticalAlignmentToAlignItems(verticalAlignment),
			isNonWrapping: isNonWrapping,
			breakpointForNonStacked: breakpointForNonStacked
		},
		nodes.map(function (x, index) {
			return React.createElement(
				'div',
				{ key: index },
				x
			);
		})
	);
};

RightAligned.propTypes = _extends({}, baseProps);

RightAligned.defaultProps = _extends({}, defaultProps);

var ThirdsWrapper = styled('div')(_templateObject6, function (props) {
	return props.isNonWrapping ? 'nowrap' : 'wrap';
}, function (props) {
	return props.alignItems;
}, function (props) {
	return props.breakpointForNonStacked && '@media (max-width: ' + props.breakpointForNonStacked + 'px) {\n\t\t\tdisplay: block;\n\t\t}';
});

var NodeWrapper = styled('div')(_templateObject7, breakpoints.sm2, function (props) {
	return !props.isNonFlexForPrint && '@media print { flex-basis: 100%; }';
});

var Thirds = function Thirds(_ref5) {
	var nodes = _ref5.nodes,
	    isNonWrapping = _ref5.isNonWrapping,
	    verticalAlignment = _ref5.verticalAlignment,
	    isNonFlexForPrint = _ref5.isNonFlexForPrint,
	    breakpointForNonStacked = _ref5.breakpointForNonStacked;
	return React.createElement(
		ThirdsWrapper,
		{
			alignItems: verticalAlignmentToAlignItems(verticalAlignment),
			isNonWrapping: isNonWrapping,
			isNonFlexForPrint: isNonFlexForPrint,
			breakpointForNonStacked: breakpointForNonStacked
		},
		nodes.map(function (x, index) {
			return React.createElement(
				NodeWrapper,
				{ key: index },
				x
			);
		})
	);
};

Thirds.propTypes = _extends({}, baseProps);

Thirds.defaultProps = _extends({}, defaultProps);

var Inline = {
	Centered: Centered,
	Justified: Justified,
	LeftAligned: LeftAligned,
	RightAligned: RightAligned,
	Thirds: Thirds
};

var _templateObject$3 = taggedTemplateLiteral(['\n\t', ';\n'], ['\n\t', ';\n']),
    _templateObject2$3 = taggedTemplateLiteral(['\n\tfont-family: ', ';\n\t', ';\n'], ['\n\tfont-family: ', ';\n\t', ';\n']),
    _templateObject3$1 = taggedTemplateLiteral(['\n\tcolor: ', ';\n\tdisplay: block;\n\tfont-family: ', ';\n\t', ';\n'], ['\n\tcolor: ', ';\n\tdisplay: block;\n\tfont-family: ', ';\n\t', ';\n']),
    _templateObject4$1 = taggedTemplateLiteral(['\n\tspan {\n\t\tdisplay: block;\n\t}\n'], ['\n\tspan {\n\t\tdisplay: block;\n\t}\n']),
    _templateObject5$1 = taggedTemplateLiteral(['\n\tbackground: none;\n\tborder: none;\n\tcolor: ', ';\n\tdisplay: block;\n\toverflow: hidden !important;\n\tpadding: ', ' 0 ', ' ', ' !important;\n\twidth: 100%;\n'], ['\n\tbackground: none;\n\tborder: none;\n\tcolor: ', ';\n\tdisplay: block;\n\toverflow: hidden !important;\n\tpadding: ', ' 0 ', ' ', ' !important;\n\twidth: 100%;\n']),
    _templateObject6$1 = taggedTemplateLiteral(['\n\tdisplay: block;\n'], ['\n\tdisplay: block;\n']),
    _templateObject7$1 = taggedTemplateLiteral(['\n\tdisplay: flex;\n\theight: 100%;\n\tpadding: ', ' ', ' ', ' 0;\n\ttext-align: left;\n\t@media (min-width: ', ') {\n\t\ttext-align: right;\n\t}\n'], ['\n\tdisplay: flex;\n\theight: 100%;\n\tpadding: ', ' ', ' ', ' 0;\n\ttext-align: left;\n\t@media (min-width: ', ') {\n\t\ttext-align: right;\n\t}\n']),
    _templateObject8 = taggedTemplateLiteral(['\n\talign-items: center;\n\tdisplay: flex;\n\theight: 100%;\n\n\tsvg {\n\t\theight: 32px;\n\t\twidth: 32px;\n\t}\n'], ['\n\talign-items: center;\n\tdisplay: flex;\n\theight: 100%;\n\n\tsvg {\n\t\theight: 32px;\n\t\twidth: 32px;\n\t}\n']),
    _templateObject9 = taggedTemplateLiteral(['\n\theight: 100%;\n\tposition: absolute;\n\tright: 0;\n\ttop: 0;\n'], ['\n\theight: 100%;\n\tposition: absolute;\n\tright: 0;\n\ttop: 0;\n']),
    _templateObject10 = taggedTemplateLiteral(['\n\ttext-align: left;\n\tmax-width: 25rem;\n\tpadding: ', ' ', ' ', ' 0;\n\n\t@media (min-width: ', ') {\n\t\tmargin-bottom: 0;\n\t}\n'], ['\n\ttext-align: left;\n\tmax-width: 25rem;\n\tpadding: ', ' ', ' ', ' 0;\n\n\t@media (min-width: ', ') {\n\t\tmargin-bottom: 0;\n\t}\n']),
    _templateObject11 = taggedTemplateLiteral(['\n\tbackground: ', ';\n\tborder: 1px solid ', ' !important;\n\tborder-left: 0 !important;\n\tborder-radius: ', ';\n\tbox-shadow: ', ';\n\tbox-sizing: border-box;\n\tcolor: ', ';\n\tposition: relative;\n\t&:before {\n\t\tbackground: ', ';\n\t\tborder-bottom-left-radius: ', ';\n\t\tborder-top-left-radius: ', ';\n\t\tcontent: \'\';\n\t\tdisplay: block;\n\t\theight: calc(100% + 2px);\n\t\tleft: 0;\n\t\tposition: absolute;\n\t\ttop: -1px;\n\t\twidth: ', ';\n\t}\n'], ['\n\tbackground: ', ';\n\tborder: 1px solid ', ' !important;\n\tborder-left: 0 !important;\n\tborder-radius: ', ';\n\tbox-shadow: ', ';\n\tbox-sizing: border-box;\n\tcolor: ', ';\n\tposition: relative;\n\t&:before {\n\t\tbackground: ', ';\n\t\tborder-bottom-left-radius: ', ';\n\t\tborder-top-left-radius: ', ';\n\t\tcontent: \'\';\n\t\tdisplay: block;\n\t\theight: calc(100% + 2px);\n\t\tleft: 0;\n\t\tposition: absolute;\n\t\ttop: -1px;\n\t\twidth: ', ';\n\t}\n']);

var StyledAccountAvailable$1 = styled('span')(_templateObject$3, useFontSize.of.uist());

var StyledAccountBalance = styled('span')(_templateObject2$3, function (props) {
	return props.theme.fonts.display.bold;
}, function (props) {
	return props.accountAvailable && 'margin-bottom: ' + spacing.SINGLE + ';';
});

var StyledAccountNameContainer = styled('div')(_templateObject3$1, function (props) {
	return props.theme.bodyColour;
}, function (props) {
	return props.theme.fonts.standardLabel;
}, function (props) {
	return props.accountAvailable && 'margin-bottom: ' + spacing.SINGLE + ';';
});

var StyledBalanceContainer = styled('div')(_templateObject4$1);

var StyledButton = styled('button')(_templateObject5$1, function (props) {
	return props.theme.bodyColour;
}, spacing.HALF, spacing.HALF, spacing.DOUBLE);

var StyledCardNumber = styled('span')(_templateObject6$1);

var StyledEndContainer = styled('div')(_templateObject7$1, spacing.HALF, spacing.QUINTUPLE, spacing.HALF, breakpoints.md1);

var StyledIconFlexWrap = styled('div')(_templateObject8);

var StyledIconWrap = styled('div')(_templateObject9);

var StyledMetaContainer = styled('div')(_templateObject$3, useFontSize.of.uist());

var StyledStartContainer = styled('div')(_templateObject10, spacing.HALF, spacing.TWO_AND_HALF, spacing.HALF, breakpoints.md1);

var StyledWrapper = styled('div')(_templateObject11, colours.component.background, colours.component.border, function (props) {
	return props.theme.borderRadius;
}, function (props) {
	return props.theme.boxShadow;
}, function (props) {
	return props.theme.colours.bodyText;
}, function (props) {
	return props.theme.colours.primary;
}, function (props) {
	return props.theme.borderRadius;
}, function (props) {
	return props.theme.borderRadius;
}, spacing.THREE_QUARTERS);

var AccountSummaryLinkCard = function AccountSummaryLinkCard(_ref) {
	var accountAvailable = _ref.accountAvailable,
	    accountBalance = _ref.accountBalance,
	    accountName = _ref.accountName,
	    accountNumber = _ref.accountNumber,
	    cardNumber = _ref.cardNumber,
	    onClick = _ref.onClick,
	    sortcode = _ref.sortcode,
	    theme = _ref.theme;

	var ChevronRightSolidSmallIcon = getChevronRightSolidSmall(theme.name);

	var start = React.createElement(
		StyledStartContainer,
		null,
		React.createElement(
			StyledAccountNameContainer,
			null,
			accountName
		),
		React.createElement(
			StyledMetaContainer,
			null,
			(sortcode || accountNumber) && React.createElement(SortCodeAndAccountNumber, { accountNumber: accountNumber, sortCode: sortcode }),
			cardNumber && React.createElement(
				StyledCardNumber,
				null,
				cardNumber
			)
		)
	);
	var end = React.createElement(
		StyledEndContainer,
		null,
		React.createElement(
			StyledBalanceContainer,
			null,
			React.createElement(
				StyledAccountBalance,
				null,
				accountBalance
			),
			accountAvailable && React.createElement(
				StyledAccountAvailable$1,
				null,
				accountAvailable,
				' available'
			)
		),
		React.createElement(
			StyledIconWrap,
			null,
			React.createElement(
				StyledIconFlexWrap,
				{ accountAvailable: accountAvailable },
				React.createElement(ChevronRightSolidSmallIcon, null)
			)
		)
	);

	return React.createElement(
		StyledWrapper,
		null,
		React.createElement(
			StyledButton,
			{ type: 'button', onClick: onClick },
			React.createElement(Inline.Justified, { nodes: [start, end] })
		)
	);
};

AccountSummaryLinkCard.propTypes = {
	/** Account available funds amount */
	accountAvailable: PropTypes.string,
	/** Account balance */
	accountBalance: PropTypes.string.isRequired,
	/** Account name */
	accountName: PropTypes.string.isRequired,
	/** Account number */
	accountNumber: PropTypes.string,
	/** Card number */
	cardNumber: PropTypes.string,
	/** Callback function triggered on click. */
	onClick: PropTypes.func.isRequired,
	/** Account sort code */
	sortcode: PropTypes.string
};

AccountSummaryLinkCard.defaultProps = {};

var WrappedAccountSummaryLinkCard = withTheme(AccountSummaryLinkCard);

var componentPaths = [
	{
		name: "ActionAnchor",
		path: "actions/anchors/ActionAnchor",
		styleguidePath: "Actions/Anchors/ActionAnchor",
		importPath: "web-ui-components/lib/actions/anchors"
	},
	{
		name: "ActionButton",
		path: "actions/buttons/ActionButton",
		styleguidePath: "Actions/Buttons/ActionButton",
		importPath: "web-ui-components/lib/actions/buttons"
	},
	{
		name: "SwitchButton",
		path: "actions/buttons/SwitchButton",
		styleguidePath: "Actions/Buttons/SwitchButton",
		importPath: "web-ui-components/lib/actions/buttons"
	},
	{
		name: "EmailLink",
		path: "actions/links/EmailLink",
		styleguidePath: "Actions/Links/EmailLink",
		importPath: "web-ui-components/lib/actions/links"
	},
	{
		name: "PhoneLink",
		path: "actions/links/PhoneLink",
		styleguidePath: "Actions/Links/PhoneLink",
		importPath: "web-ui-components/lib/actions/links"
	},
	{
		name: "WebsiteLink",
		path: "actions/links/WebsiteLink",
		styleguidePath: "Actions/Links/WebsiteLink",
		importPath: "web-ui-components/lib/actions/links"
	},
	{
		name: "InputRadio",
		path: "actions/radios/InputRadio",
		styleguidePath: "Actions/Radios/InputRadio",
		importPath: "web-ui-components/lib/actions/radios"
	},
	{
		name: "InputRadioGroup",
		path: "actions/radios/InputRadioGroup",
		styleguidePath: "Actions/Radios/InputRadioGroup",
		importPath: "web-ui-components/lib/actions/radios"
	},
	{
		name: "TallRadioCard",
		path: "actions/radios/TallRadioCard",
		styleguidePath: "Actions/Radios/TallRadioCard",
		importPath: "web-ui-components/lib/actions/radios"
	},
	{
		name: "WideRadioCard",
		path: "actions/radios/WideRadioCard",
		styleguidePath: "Actions/Radios/WideRadioCard",
		importPath: "web-ui-components/lib/actions/radios"
	},
	{
		name: "NewWindowAction",
		path: "advisor/actions/NewWindowAction",
		styleguidePath: "Advisor/Actions/NewWindowAction",
		importPath: "web-ui-components/lib/advisor/actions"
	},
	{
		name: "CustomerAlert",
		path: "advisor/alerts/CustomerAlert",
		styleguidePath: "Advisor/Alerts/CustomerAlert",
		importPath: "web-ui-components/lib/advisor/alerts"
	},
	{
		name: "ClickableContentFrame",
		path: "advisor/layout/ClickableContentFrame",
		styleguidePath: "Advisor/Layout/ClickableContentFrame",
		importPath: "web-ui-components/lib/advisor/layout"
	},
	{
		name: "DashboardGrid",
		path: "advisor/layout/DashboardGrid",
		styleguidePath: "Advisor/Layout/DashboardGrid",
		importPath: "web-ui-components/lib/advisor/layout"
	},
	{
		name: "WideModal",
		path: "advisor/layout/WideModal",
		styleguidePath: "Advisor/Layout/WideModal",
		importPath: "web-ui-components/lib/advisor/layout"
	},
	{
		name: "WindowFrame",
		path: "advisor/layout/WindowFrame",
		styleguidePath: "Advisor/Layout/WindowFrame",
		importPath: "web-ui-components/lib/advisor/layout"
	},
	{
		name: "ProductRadioCard",
		path: "advisor/radios/ProductRadioCard",
		styleguidePath: "Advisor/Radios/ProductRadioCard",
		importPath: "web-ui-components/lib/advisor/radios"
	},
	{
		name: "AccountSelect",
		path: "advisor/selects/AccountSelect",
		styleguidePath: "Advisor/Selects/AccountSelect",
		importPath: "web-ui-components/lib/advisor/selects"
	},
	{
		name: "SessionTimeoutTakeover",
		path: "advisor/takeovers/SessionTimeoutTakeover",
		styleguidePath: "Advisor/Takeovers/SessionTimeoutTakeover",
		importPath: "web-ui-components/lib/advisor/takeovers"
	},
	{
		name: "Anchor",
		path: "atoms/buttons/Anchor",
		styleguidePath: "Atoms/Buttons/Anchor",
		importPath: "web-ui-components/lib/atoms/buttons"
	},
	{
		name: "Button",
		path: "atoms/buttons/Button",
		styleguidePath: "Atoms/Buttons/Button",
		importPath: "web-ui-components/lib/atoms/buttons"
	},
	{
		name: "ButtonTitle",
		path: "atoms/buttons/ButtonTitle",
		styleguidePath: "Atoms/Buttons/ButtonTitle",
		importPath: "web-ui-components/lib/atoms/buttons"
	},
	{
		name: "Calendar",
		path: "atoms/forms/Calendar",
		styleguidePath: "Atoms/Forms/Calendar",
		importPath: "web-ui-components/lib/atoms/forms"
	},
	{
		name: "Checkbox",
		path: "atoms/forms/Checkbox",
		styleguidePath: "Atoms/Forms/Checkbox",
		importPath: "web-ui-components/lib/atoms/forms"
	},
	{
		name: "CheckboxToggle",
		path: "atoms/forms/CheckboxToggle",
		styleguidePath: "Atoms/Forms/CheckboxToggle",
		importPath: "web-ui-components/lib/atoms/forms"
	},
	{
		name: "Datepicker",
		path: "atoms/forms/Datepicker",
		styleguidePath: "Atoms/Forms/Datepicker",
		importPath: "web-ui-components/lib/atoms/forms"
	},
	{
		name: "FieldDescription",
		path: "atoms/forms/FieldDescription",
		styleguidePath: "Atoms/Forms/FieldDescription",
		importPath: "web-ui-components/lib/atoms/forms"
	},
	{
		name: "Input",
		path: "atoms/forms/Input/Input",
		styleguidePath: "Atoms/Forms/Input/Input",
		importPath: "web-ui-components/lib/atoms/forms/input"
	},
	{
		name: "Prefix",
		path: "atoms/forms/Input/Prefix",
		styleguidePath: "Atoms/Forms/Input/Prefix",
		importPath: "web-ui-components/lib/atoms/forms/input"
	},
	{
		name: "Suffix",
		path: "atoms/forms/Input/Suffix",
		styleguidePath: "Atoms/Forms/Input/Suffix",
		importPath: "web-ui-components/lib/atoms/forms/input"
	},
	{
		name: "TextPrompt",
		path: "atoms/forms/Input/TextPrompt",
		styleguidePath: "Atoms/Forms/Input/TextPrompt",
		importPath: "web-ui-components/lib/atoms/forms/input"
	},
	{
		name: "InputClickable",
		path: "atoms/forms/InputClickable",
		styleguidePath: "Atoms/Forms/InputClickable",
		importPath: "web-ui-components/lib/atoms/forms"
	},
	{
		name: "InputRange",
		path: "atoms/forms/InputRange",
		styleguidePath: "Atoms/Forms/InputRange",
		importPath: "web-ui-components/lib/atoms/forms"
	},
	{
		name: "LabelTitle",
		path: "atoms/forms/Label/LabelTitle",
		styleguidePath: "Atoms/Forms/Label/LabelTitle",
		importPath: "web-ui-components/lib/atoms/forms/label"
	},
	{
		name: "Label",
		path: "atoms/forms/Label",
		styleguidePath: "Atoms/Forms/Label",
		importPath: "web-ui-components/lib/atoms/forms"
	},
	{
		name: "MediaCheckbox",
		path: "atoms/forms/MediaCheckbox",
		styleguidePath: "Atoms/Forms/MediaCheckbox",
		importPath: "web-ui-components/lib/atoms/forms"
	},
	{
		name: "MediaRadio",
		path: "atoms/forms/MediaRadio",
		styleguidePath: "Atoms/Forms/MediaRadio",
		importPath: "web-ui-components/lib/atoms/forms"
	},
	{
		name: "MultiSelect",
		path: "atoms/forms/MultiSelect",
		styleguidePath: "Atoms/Forms/MultiSelect",
		importPath: "web-ui-components/lib/atoms/forms"
	},
	{
		name: "Radio",
		path: "atoms/forms/Radio",
		styleguidePath: "Atoms/Forms/Radio",
		importPath: "web-ui-components/lib/atoms/forms"
	},
	{
		name: "RadioCard",
		path: "atoms/forms/RadioCard",
		styleguidePath: "Atoms/Forms/RadioCard",
		importPath: "web-ui-components/lib/atoms/forms"
	},
	{
		name: "Select",
		path: "atoms/forms/Select",
		styleguidePath: "Atoms/Forms/Select",
		importPath: "web-ui-components/lib/atoms/forms"
	},
	{
		name: "Textarea",
		path: "atoms/forms/Textarea",
		styleguidePath: "Atoms/Forms/Textarea",
		importPath: "web-ui-components/lib/atoms/forms"
	},
	{
		name: "TextAreaCharacterCount",
		path: "atoms/forms/TextAreaCharacterCount",
		styleguidePath: "Atoms/Forms/TextAreaCharacterCount",
		importPath: "web-ui-components/lib/atoms/forms"
	},
	{
		name: "TimeEstimationIcon",
		path: "atoms/forms/TimeEstimationIcon",
		styleguidePath: "Atoms/Forms/TimeEstimationIcon",
		importPath: "web-ui-components/lib/atoms/forms"
	},
	{
		name: "AnimatedIcon",
		path: "atoms/iconography/AnimatedIcon",
		styleguidePath: "Atoms/Iconography/AnimatedIcon",
		importPath: "web-ui-components/lib/atoms/iconography"
	},
	{
		name: "DistinctiveGeneratedSymbol",
		path: "atoms/iconography/DistinctiveGeneratedSymbol",
		styleguidePath: "Atoms/Iconography/DistinctiveGeneratedSymbol",
		importPath: "web-ui-components/lib/atoms/iconography"
	},
	{
		name: "Icon",
		path: "atoms/iconography/Icon",
		styleguidePath: "Atoms/Iconography/Icon",
		importPath: "web-ui-components/lib/atoms/iconography"
	},
	{
		name: "Divider",
		path: "atoms/layout/Divider",
		styleguidePath: "Atoms/Layout/Divider",
		importPath: "web-ui-components/lib/atoms/layout"
	},
	{
		name: "UploadPreviewContainer",
		path: "atoms/layout/UploadPreviewContainer",
		styleguidePath: "Atoms/Layout/UploadPreviewContainer",
		importPath: "web-ui-components/lib/atoms/layout"
	},
	{
		name: "AndroidStoreLogo",
		path: "atoms/logo/AndroidStoreLogo",
		styleguidePath: "Atoms/Logo/AndroidStoreLogo",
		importPath: "web-ui-components/lib/atoms/logo"
	},
	{
		name: "AppleStoreLogo",
		path: "atoms/logo/AppleStoreLogo",
		styleguidePath: "Atoms/Logo/AppleStoreLogo",
		importPath: "web-ui-components/lib/atoms/logo"
	},
	{
		name: "BankHeader",
		path: "atoms/logo/BankHeader",
		styleguidePath: "Atoms/Logo/BankHeader",
		importPath: "web-ui-components/lib/atoms/logo"
	},
	{
		name: "BLogo",
		path: "atoms/logo/BLogo",
		styleguidePath: "Atoms/Logo/BLogo",
		importPath: "web-ui-components/lib/atoms/logo"
	},
	{
		name: "BLogoCircular",
		path: "atoms/logo/BLogoCircular",
		styleguidePath: "Atoms/Logo/BLogoCircular",
		importPath: "web-ui-components/lib/atoms/logo"
	},
	{
		name: "CBLogo",
		path: "atoms/logo/CBLogo",
		styleguidePath: "Atoms/Logo/CBLogo",
		importPath: "web-ui-components/lib/atoms/logo"
	},
	{
		name: "CBLogoCircular",
		path: "atoms/logo/CBLogoCircular",
		styleguidePath: "Atoms/Logo/CBLogoCircular",
		importPath: "web-ui-components/lib/atoms/logo"
	},
	{
		name: "Header",
		path: "atoms/logo/Header",
		styleguidePath: "Atoms/Logo/Header",
		importPath: "web-ui-components/lib/atoms/logo"
	},
	{
		name: "SwitchGuaranteeLogo",
		path: "atoms/logo/SwitchGuaranteeLogo",
		styleguidePath: "Atoms/Logo/SwitchGuaranteeLogo",
		importPath: "web-ui-components/lib/atoms/logo"
	},
	{
		name: "VMSecondaryRedLogo",
		path: "atoms/logo/VMSecondaryRedLogo",
		styleguidePath: "Atoms/Logo/VMSecondaryRedLogo",
		importPath: "web-ui-components/lib/atoms/logo"
	},
	{
		name: "YBLogo",
		path: "atoms/logo/YBLogo",
		styleguidePath: "Atoms/Logo/YBLogo",
		importPath: "web-ui-components/lib/atoms/logo"
	},
	{
		name: "YBLogoCircular",
		path: "atoms/logo/YBLogoCircular",
		styleguidePath: "Atoms/Logo/YBLogoCircular",
		importPath: "web-ui-components/lib/atoms/logo"
	},
	{
		name: "Popover",
		path: "atoms/popovers/Popover",
		styleguidePath: "Atoms/Popovers/Popover",
		importPath: "web-ui-components/lib/atoms/popovers"
	},
	{
		name: "IdentityProcessingSignal",
		path: "atoms/signals/IdentityProcessingSignal",
		styleguidePath: "Atoms/Signals/IdentityProcessingSignal",
		importPath: "web-ui-components/lib/atoms/signals"
	},
	{
		name: "UploadProcessingSignal",
		path: "atoms/signals/UploadProcessingSignal",
		styleguidePath: "Atoms/Signals/UploadProcessingSignal",
		importPath: "web-ui-components/lib/atoms/signals"
	},
	{
		name: "BadgeLabel",
		path: "atoms/text/BadgeLabel",
		styleguidePath: "Atoms/Text/BadgeLabel",
		importPath: "web-ui-components/lib/atoms/text"
	},
	{
		name: "BulletedList",
		path: "atoms/text/BulletedList",
		styleguidePath: "Atoms/Text/BulletedList",
		importPath: "web-ui-components/lib/atoms/text"
	},
	{
		name: "Callout",
		path: "atoms/text/Callout",
		styleguidePath: "Atoms/Text/Callout",
		importPath: "web-ui-components/lib/atoms/text"
	},
	{
		name: "List",
		path: "atoms/text/List",
		styleguidePath: "Atoms/Text/List",
		importPath: "web-ui-components/lib/atoms/text"
	},
	{
		name: "NumberedList",
		path: "atoms/text/NumberedList",
		styleguidePath: "Atoms/Text/NumberedList",
		importPath: "web-ui-components/lib/atoms/text"
	},
	{
		name: "StatusBadge",
		path: "atoms/text/StatusBadge",
		styleguidePath: "Atoms/Text/StatusBadge",
		importPath: "web-ui-components/lib/atoms/text"
	},
	{
		name: "TextBody",
		path: "atoms/text/TextBody",
		styleguidePath: "Atoms/Text/TextBody",
		importPath: "web-ui-components/lib/atoms/text"
	},
	{
		name: "TextStyled",
		path: "atoms/text/TextStyled",
		styleguidePath: "Atoms/Text/TextStyled",
		importPath: "web-ui-components/lib/atoms/text"
	},
	{
		name: "TheSmallPrint",
		path: "atoms/text/TheSmallPrint",
		styleguidePath: "Atoms/Text/TheSmallPrint",
		importPath: "web-ui-components/lib/atoms/text"
	},
	{
		name: "Title",
		path: "atoms/text/Title",
		styleguidePath: "Atoms/Text/Title",
		importPath: "web-ui-components/lib/atoms/text"
	},
	{
		name: "TransitionBlock",
		path: "atoms/transitions/TransitionBlock",
		styleguidePath: "Atoms/Transitions/TransitionBlock",
		importPath: "web-ui-components/lib/atoms/transitions"
	},
	{
		name: "EmailInput",
		path: "communication/email/text-inputs/EmailInput",
		styleguidePath: "Communication/Email/Text-inputs/EmailInput",
		importPath: "web-ui-components/lib/communication/email/text-inputs"
	},
	{
		name: "Mail",
		path: "communication/messaging/Mail",
		styleguidePath: "Communication/Messaging/Mail",
		importPath: "web-ui-components/lib/communication/messaging"
	},
	{
		name: "Webchat",
		path: "communication/support/Webchat",
		styleguidePath: "Communication/Support/Webchat",
		importPath: "web-ui-components/lib/communication/support"
	},
	{
		name: "CheckboxContainer",
		path: "data/capture/checkboxes/CheckboxContainer",
		styleguidePath: "Data/Capture/Checkboxes/CheckboxContainer",
		importPath: "web-ui-components/lib/data/capture/checkboxes"
	},
	{
		name: "GenericCheckbox",
		path: "data/capture/checkboxes/GenericCheckbox",
		styleguidePath: "Data/Capture/Checkboxes/GenericCheckbox",
		importPath: "web-ui-components/lib/data/capture/checkboxes"
	},
	{
		name: "StandaloneCheckbox",
		path: "data/capture/checkboxes/StandaloneCheckbox",
		styleguidePath: "Data/Capture/Checkboxes/StandaloneCheckbox",
		importPath: "web-ui-components/lib/data/capture/checkboxes"
	},
	{
		name: "AccountNumberInput",
		path: "data/capture/inputs/AccountNumberInput",
		styleguidePath: "Data/Capture/Inputs/AccountNumberInput",
		importPath: "web-ui-components/lib/data/capture/inputs"
	},
	{
		name: "DateOfBirthInput",
		path: "data/capture/inputs/DateOfBirthInput",
		styleguidePath: "Data/Capture/Inputs/DateOfBirthInput",
		importPath: "web-ui-components/lib/data/capture/inputs"
	},
	{
		name: "FileUploadInput",
		path: "data/capture/inputs/FileUploadInput",
		styleguidePath: "Data/Capture/Inputs/FileUploadInput",
		importPath: "web-ui-components/lib/data/capture/inputs"
	},
	{
		name: "GenericAlphaNumericInput",
		path: "data/capture/inputs/GenericAlphaNumericInput",
		styleguidePath: "Data/Capture/Inputs/GenericAlphaNumericInput",
		importPath: "web-ui-components/lib/data/capture/inputs"
	},
	{
		name: "GenericIntegerInput",
		path: "data/capture/inputs/GenericIntegerInput",
		styleguidePath: "Data/Capture/Inputs/GenericIntegerInput",
		importPath: "web-ui-components/lib/data/capture/inputs"
	},
	{
		name: "GenericTextInput",
		path: "data/capture/inputs/GenericTextInput",
		styleguidePath: "Data/Capture/Inputs/GenericTextInput",
		importPath: "web-ui-components/lib/data/capture/inputs"
	},
	{
		name: "LandlineNumberInput",
		path: "data/capture/inputs/LandlineNumberInput",
		styleguidePath: "Data/Capture/Inputs/LandlineNumberInput",
		importPath: "web-ui-components/lib/data/capture/inputs"
	},
	{
		name: "MoneyInput",
		path: "data/capture/inputs/MoneyInput",
		styleguidePath: "Data/Capture/Inputs/MoneyInput",
		importPath: "web-ui-components/lib/data/capture/inputs"
	},
	{
		name: "MonthAndYearDateInput",
		path: "data/capture/inputs/MonthAndYearDateInput",
		styleguidePath: "Data/Capture/Inputs/MonthAndYearDateInput",
		importPath: "web-ui-components/lib/data/capture/inputs"
	},
	{
		name: "PasscodeNumberInput",
		path: "data/capture/inputs/PasscodeNumberInput",
		styleguidePath: "Data/Capture/Inputs/PasscodeNumberInput",
		importPath: "web-ui-components/lib/data/capture/inputs"
	},
	{
		name: "SliderInput",
		path: "data/capture/inputs/SliderInput",
		styleguidePath: "Data/Capture/Inputs/SliderInput",
		importPath: "web-ui-components/lib/data/capture/inputs"
	},
	{
		name: "SortcodeInput",
		path: "data/capture/inputs/SortcodeInput",
		styleguidePath: "Data/Capture/Inputs/SortcodeInput",
		importPath: "web-ui-components/lib/data/capture/inputs"
	},
	{
		name: "UKOnlyMobileNumberInput",
		path: "data/capture/inputs/UKOnlyMobileNumberInput",
		styleguidePath: "Data/Capture/Inputs/UKOnlyMobileNumberInput",
		importPath: "web-ui-components/lib/data/capture/inputs"
	},
	{
		name: "UKOnlyPostcodeInput",
		path: "data/capture/inputs/UKOnlyPostcodeInput",
		styleguidePath: "Data/Capture/Inputs/UKOnlyPostcodeInput",
		importPath: "web-ui-components/lib/data/capture/inputs"
	},
	{
		name: "WorldwideMobileNumberInput",
		path: "data/capture/inputs/WorldwideMobileNumberInput",
		styleguidePath: "Data/Capture/Inputs/WorldwideMobileNumberInput",
		importPath: "web-ui-components/lib/data/capture/inputs"
	},
	{
		name: "YearsAndMonthsCountInput",
		path: "data/capture/inputs/YearsAndMonthsCountInput",
		styleguidePath: "Data/Capture/Inputs/YearsAndMonthsCountInput",
		importPath: "web-ui-components/lib/data/capture/inputs"
	},
	{
		name: "DayOfMonthPicker",
		path: "data/capture/pickers/DayOfMonthPicker",
		styleguidePath: "Data/Capture/Pickers/DayOfMonthPicker",
		importPath: "web-ui-components/lib/data/capture/pickers"
	},
	{
		name: "FileProgressTakeover",
		path: "data/capture/progress/FileProgressTakeover",
		styleguidePath: "Data/Capture/Progress/FileProgressTakeover",
		importPath: "web-ui-components/lib/data/capture/progress"
	},
	{
		name: "GenericRadio",
		path: "data/capture/radios/GenericRadio",
		styleguidePath: "Data/Capture/Radios/GenericRadio",
		importPath: "web-ui-components/lib/data/capture/radios"
	},
	{
		name: "IconifiedRadio",
		path: "data/capture/radios/IconifiedRadio",
		styleguidePath: "Data/Capture/Radios/IconifiedRadio",
		importPath: "web-ui-components/lib/data/capture/radios"
	},
	{
		name: "RadioContainer",
		path: "data/capture/radios/RadioContainer",
		styleguidePath: "Data/Capture/Radios/RadioContainer",
		importPath: "web-ui-components/lib/data/capture/radios"
	},
	{
		name: "AccountCardSelect",
		path: "data/capture/selects/AccountCardSelect",
		styleguidePath: "Data/Capture/Selects/AccountCardSelect",
		importPath: "web-ui-components/lib/data/capture/selects"
	},
	{
		name: "GenericSelect",
		path: "data/capture/selects/GenericSelect",
		styleguidePath: "Data/Capture/Selects/GenericSelect",
		importPath: "web-ui-components/lib/data/capture/selects"
	},
	{
		name: "GenericTextArea",
		path: "data/capture/text-areas/GenericTextArea",
		styleguidePath: "Data/Capture/Text-areas/GenericTextArea",
		importPath: "web-ui-components/lib/data/capture/text-areas"
	},
	{
		name: "InternationalMoneyValue",
		path: "data/display/values/InternationalMoneyValue",
		styleguidePath: "Data/Display/Values/InternationalMoneyValue",
		importPath: "web-ui-components/lib/data/display/values"
	},
	{
		name: "MoneyValue",
		path: "data/display/values/MoneyValue",
		styleguidePath: "Data/Display/Values/MoneyValue",
		importPath: "web-ui-components/lib/data/display/values"
	},
	{
		name: "GenericPaginator",
		path: "data/filter-and-search/paginators/GenericPaginator",
		styleguidePath: "Data/Filter-and-search/Paginators/GenericPaginator",
		importPath: "web-ui-components/lib/data/filter-and-search/paginators"
	},
	{
		name: "SearchSelectInput",
		path: "data/filter-and-search/text-inputs/SearchSelectInput",
		styleguidePath: "Data/Filter-and-search/Text-inputs/SearchSelectInput",
		importPath: "web-ui-components/lib/data/filter-and-search/text-inputs"
	},
	{
		name: "SearchTextInput",
		path: "data/filter-and-search/text-inputs/SearchTextInput",
		styleguidePath: "Data/Filter-and-search/Text-inputs/SearchTextInput",
		importPath: "web-ui-components/lib/data/filter-and-search/text-inputs"
	},
	{
		name: "CompactDescriptionList",
		path: "data/lists/description-lists/CompactDescriptionList",
		styleguidePath: "Data/Lists/Description-lists/CompactDescriptionList",
		importPath: "web-ui-components/lib/data/lists/description-lists"
	},
	{
		name: "GenericDescriptionList",
		path: "data/lists/description-lists/GenericDescriptionList",
		styleguidePath: "Data/Lists/Description-lists/GenericDescriptionList",
		importPath: "web-ui-components/lib/data/lists/description-lists"
	},
	{
		name: "NoResultsTile",
		path: "data/results/tiles/NoResultsTile",
		styleguidePath: "Data/Results/Tiles/NoResultsTile",
		importPath: "web-ui-components/lib/data/results/tiles"
	},
	{
		name: "DocumentLink",
		path: "documents/navigation/links/DocumentLink",
		styleguidePath: "Documents/Navigation/Links/DocumentLink",
		importPath: "web-ui-components/lib/documents/navigation/links"
	},
	{
		name: "DocumentLinkPlate",
		path: "documents/navigation/plates/DocumentLinkPlate",
		styleguidePath: "Documents/Navigation/Plates/DocumentLinkPlate",
		importPath: "web-ui-components/lib/documents/navigation/plates"
	},
	{
		name: "PrintButton",
		path: "documents/printing/buttons/PrintButton",
		styleguidePath: "Documents/Printing/Buttons/PrintButton",
		importPath: "web-ui-components/lib/documents/printing/buttons"
	},
	{
		name: "DocumentStatusBandCard",
		path: "documents/statuses/cards/DocumentStatusBandCard",
		styleguidePath: "Documents/Statuses/Cards/DocumentStatusBandCard",
		importPath: "web-ui-components/lib/documents/statuses/cards"
	},
	{
		name: "FileInput",
		path: "documents/upload/FileInput",
		styleguidePath: "Documents/Upload/FileInput",
		importPath: "web-ui-components/lib/documents/upload"
	},
	{
		name: "AccountCheckboxCard",
		path: "finance/accounts/cards/AccountCheckboxCard",
		styleguidePath: "Finance/Accounts/Cards/AccountCheckboxCard",
		importPath: "web-ui-components/lib/finance/accounts/cards"
	},
	{
		name: "AccountLegacyUpdateCard",
		path: "finance/accounts/cards/AccountLegacyUpdateCard",
		styleguidePath: "Finance/Accounts/Cards/AccountLegacyUpdateCard",
		importPath: "web-ui-components/lib/finance/accounts/cards"
	},
	{
		name: "AccountLinkCard",
		path: "finance/accounts/cards/AccountLinkCard",
		styleguidePath: "Finance/Accounts/Cards/AccountLinkCard",
		importPath: "web-ui-components/lib/finance/accounts/cards"
	},
	{
		name: "AccountPoolCard",
		path: "finance/accounts/cards/AccountPoolCard",
		styleguidePath: "Finance/Accounts/Cards/AccountPoolCard",
		importPath: "web-ui-components/lib/finance/accounts/cards"
	},
	{
		name: "AccountSelectCard",
		path: "finance/accounts/cards/AccountSelectCard",
		styleguidePath: "Finance/Accounts/Cards/AccountSelectCard",
		importPath: "web-ui-components/lib/finance/accounts/cards"
	},
	{
		name: "AccountStaticCard",
		path: "finance/accounts/cards/AccountStaticCard",
		styleguidePath: "Finance/Accounts/Cards/AccountStaticCard",
		importPath: "web-ui-components/lib/finance/accounts/cards"
	},
	{
		name: "AccountAuthorisationPanel",
		path: "finance/accounts/panels/AccountAuthorisationPanel",
		styleguidePath: "Finance/Accounts/Panels/AccountAuthorisationPanel",
		importPath: "web-ui-components/lib/finance/accounts/panels"
	},
	{
		name: "AccountBalancesSelectPanel",
		path: "finance/accounts/panels/AccountBalancesSelectPanel",
		styleguidePath: "Finance/Accounts/Panels/AccountBalancesSelectPanel",
		importPath: "web-ui-components/lib/finance/accounts/panels"
	},
	{
		name: "AccountLegacyPanel",
		path: "finance/accounts/panels/AccountLegacyPanel",
		styleguidePath: "Finance/Accounts/Panels/AccountLegacyPanel",
		importPath: "web-ui-components/lib/finance/accounts/panels"
	},
	{
		name: "AccountPaymentStatusPanel",
		path: "finance/accounts/panels/AccountPaymentStatusPanel",
		styleguidePath: "Finance/Accounts/Panels/AccountPaymentStatusPanel",
		importPath: "web-ui-components/lib/finance/accounts/panels"
	},
	{
		name: "AccountPoolTablePanel",
		path: "finance/accounts/panels/AccountPoolTablePanel",
		styleguidePath: "Finance/Accounts/Panels/AccountPoolTablePanel",
		importPath: "web-ui-components/lib/finance/accounts/panels"
	},
	{
		name: "AccountRadioCard",
		path: "finance/accounts/radios/AccountRadioCard",
		styleguidePath: "Finance/Accounts/Radios/AccountRadioCard",
		importPath: "web-ui-components/lib/finance/accounts/radios"
	},
	{
		name: "AccountPaymentReviewTable",
		path: "finance/accounts/tables/AccountPaymentReviewTable",
		styleguidePath: "Finance/Accounts/Tables/AccountPaymentReviewTable",
		importPath: "web-ui-components/lib/finance/accounts/tables"
	},
	{
		name: "AccountPaymentSubmittedTable",
		path: "finance/accounts/tables/AccountPaymentSubmittedTable",
		styleguidePath: "Finance/Accounts/Tables/AccountPaymentSubmittedTable",
		importPath: "web-ui-components/lib/finance/accounts/tables"
	},
	{
		name: "AccountPaymentTable",
		path: "finance/accounts/tables/AccountPaymentTable",
		styleguidePath: "Finance/Accounts/Tables/AccountPaymentTable",
		importPath: "web-ui-components/lib/finance/accounts/tables"
	},
	{
		name: "AccountSummaryTile",
		path: "finance/accounts/tiles/AccountSummaryTile",
		styleguidePath: "Finance/Accounts/Tiles/AccountSummaryTile",
		importPath: "web-ui-components/lib/finance/accounts/tiles"
	},
	{
		name: "CurrencySelect",
		path: "finance/currency/selects/CurrencySelect",
		styleguidePath: "Finance/Currency/Selects/CurrencySelect",
		importPath: "web-ui-components/lib/finance/currency/selects"
	},
	{
		name: "BlockField",
		path: "global/layout/BlockField",
		styleguidePath: "Global/Layout/BlockField",
		importPath: "web-ui-components/lib/global/layout"
	},
	{
		name: "BlockFieldColumn",
		path: "global/layout/BlockFieldColumn",
		styleguidePath: "Global/Layout/BlockFieldColumn",
		importPath: "web-ui-components/lib/global/layout"
	},
	{
		name: "CenterImageContainer",
		path: "global/layout/CenterImageContainer",
		styleguidePath: "Global/Layout/CenterImageContainer",
		importPath: "web-ui-components/lib/global/layout"
	},
	{
		name: "Column",
		path: "global/layout/Column",
		styleguidePath: "Global/Layout/Column",
		importPath: "web-ui-components/lib/global/layout"
	},
	{
		name: "Container",
		path: "global/layout/Container",
		styleguidePath: "Global/Layout/Container",
		importPath: "web-ui-components/lib/global/layout"
	},
	{
		name: "FlexColumn",
		path: "global/layout/FlexColumn",
		styleguidePath: "Global/Layout/FlexColumn",
		importPath: "web-ui-components/lib/global/layout"
	},
	{
		name: "FlexRow",
		path: "global/layout/FlexRow",
		styleguidePath: "Global/Layout/FlexRow",
		importPath: "web-ui-components/lib/global/layout"
	},
	{
		name: "Frame",
		path: "global/layout/Frame",
		styleguidePath: "Global/Layout/Frame",
		importPath: "web-ui-components/lib/global/layout"
	},
	{
		name: "InternalSlot",
		path: "global/layout/InternalSlot",
		styleguidePath: "Global/Layout/InternalSlot",
		importPath: "web-ui-components/lib/global/layout"
	},
	{
		name: "LeftAndRightAlignment",
		path: "global/layout/LeftAndRightAlignment",
		styleguidePath: "Global/Layout/LeftAndRightAlignment",
		importPath: "web-ui-components/lib/global/layout"
	},
	{
		name: "PageLayout",
		path: "global/layout/PageLayout",
		styleguidePath: "Global/Layout/PageLayout",
		importPath: "web-ui-components/lib/global/layout"
	},
	{
		name: "HeaderSection",
		path: "global/layout/Pages/HeaderSection",
		styleguidePath: "Global/Layout/Pages/HeaderSection",
		importPath: "web-ui-components/lib/global/layout/pages"
	},
	{
		name: "ServicingSection",
		path: "global/layout/Pages/ServicingSection",
		styleguidePath: "Global/Layout/Pages/ServicingSection",
		importPath: "web-ui-components/lib/global/layout/pages"
	},
	{
		name: "WorkflowSection",
		path: "global/layout/Pages/WorkflowSection",
		styleguidePath: "Global/Layout/Pages/WorkflowSection",
		importPath: "web-ui-components/lib/global/layout/pages"
	},
	{
		name: "ResponsiveLayoutPair",
		path: "global/layout/ResponsiveLayoutPair",
		styleguidePath: "Global/Layout/ResponsiveLayoutPair",
		importPath: "web-ui-components/lib/global/layout"
	},
	{
		name: "Row",
		path: "global/layout/Row",
		styleguidePath: "Global/Layout/Row",
		importPath: "web-ui-components/lib/global/layout"
	},
	{
		name: "ContentContainer",
		path: "layout/containers/ContentContainer",
		styleguidePath: "Layout/Containers/ContentContainer",
		importPath: "web-ui-components/lib/layout/containers"
	},
	{
		name: "Main",
		path: "layout/containers/Main",
		styleguidePath: "Layout/Containers/Main",
		importPath: "web-ui-components/lib/layout/containers"
	},
	{
		name: "GenericAccordion",
		path: "layout/content/accordions/GenericAccordion",
		styleguidePath: "Layout/Content/Accordions/GenericAccordion",
		importPath: "web-ui-components/lib/layout/content/accordions"
	},
	{
		name: "StatusBanner",
		path: "layout/content/banners/StatusBanner",
		styleguidePath: "Layout/Content/Banners/StatusBanner",
		importPath: "web-ui-components/lib/layout/content/banners"
	},
	{
		name: "GenericDivider",
		path: "layout/content/breaks/GenericDivider",
		styleguidePath: "Layout/Content/Breaks/GenericDivider",
		importPath: "web-ui-components/lib/layout/content/breaks"
	},
	{
		name: "GenericModal",
		path: "layout/content/modals/GenericModal",
		styleguidePath: "Layout/Content/Modals/GenericModal",
		importPath: "web-ui-components/lib/layout/content/modals"
	},
	{
		name: "InformationModal",
		path: "layout/content/modals/InformationModal",
		styleguidePath: "Layout/Content/Modals/InformationModal",
		importPath: "web-ui-components/lib/layout/content/modals"
	},
	{
		name: "MultiButtonModal",
		path: "layout/content/modals/MultiButtonModal",
		styleguidePath: "Layout/Content/Modals/MultiButtonModal",
		importPath: "web-ui-components/lib/layout/content/modals"
	},
	{
		name: "BrandedPanel",
		path: "layout/content/panels/BrandedPanel",
		styleguidePath: "Layout/Content/Panels/BrandedPanel",
		importPath: "web-ui-components/lib/layout/content/panels"
	},
	{
		name: "ConditionalPanel",
		path: "layout/content/panels/ConditionalPanel",
		styleguidePath: "Layout/Content/Panels/ConditionalPanel",
		importPath: "web-ui-components/lib/layout/content/panels"
	},
	{
		name: "ContentRevealPanel",
		path: "layout/content/panels/ContentRevealPanel",
		styleguidePath: "Layout/Content/Panels/ContentRevealPanel",
		importPath: "web-ui-components/lib/layout/content/panels"
	},
	{
		name: "GenericPanel",
		path: "layout/content/panels/GenericPanel",
		styleguidePath: "Layout/Content/Panels/GenericPanel",
		importPath: "web-ui-components/lib/layout/content/panels"
	},
	{
		name: "InformationRevealPanel",
		path: "layout/content/panels/InformationRevealPanel",
		styleguidePath: "Layout/Content/Panels/InformationRevealPanel",
		importPath: "web-ui-components/lib/layout/content/panels"
	},
	{
		name: "ShellPanel",
		path: "layout/content/panels/ShellPanel",
		styleguidePath: "Layout/Content/Panels/ShellPanel",
		importPath: "web-ui-components/lib/layout/content/panels"
	},
	{
		name: "ShellSidebarPanel",
		path: "layout/content/panels/ShellSidebarPanel",
		styleguidePath: "Layout/Content/Panels/ShellSidebarPanel",
		importPath: "web-ui-components/lib/layout/content/panels"
	},
	{
		name: "SimplePanel",
		path: "layout/content/panels/SimplePanel",
		styleguidePath: "Layout/Content/Panels/SimplePanel",
		importPath: "web-ui-components/lib/layout/content/panels"
	},
	{
		name: "StatusPanel",
		path: "layout/content/panels/StatusPanel",
		styleguidePath: "Layout/Content/Panels/StatusPanel",
		importPath: "web-ui-components/lib/layout/content/panels"
	},
	{
		name: "SubformRevealPanel",
		path: "layout/content/panels/SubformRevealPanel",
		styleguidePath: "Layout/Content/Panels/SubformRevealPanel",
		importPath: "web-ui-components/lib/layout/content/panels"
	},
	{
		name: "ToggleButtonPanel",
		path: "layout/content/panels/ToggleButtonPanel",
		styleguidePath: "Layout/Content/Panels/ToggleButtonPanel",
		importPath: "web-ui-components/lib/layout/content/panels"
	},
	{
		name: "ProcessingScreenTakeover",
		path: "layout/content/takeovers/ProcessingScreenTakeover",
		styleguidePath: "Layout/Content/Takeovers/ProcessingScreenTakeover",
		importPath: "web-ui-components/lib/layout/content/takeovers"
	},
	{
		name: "AttentionTile",
		path: "layout/content/tiles/AttentionTile",
		styleguidePath: "Layout/Content/Tiles/AttentionTile",
		importPath: "web-ui-components/lib/layout/content/tiles"
	},
	{
		name: "GuidanceTile",
		path: "layout/content/tiles/GuidanceTile",
		styleguidePath: "Layout/Content/Tiles/GuidanceTile",
		importPath: "web-ui-components/lib/layout/content/tiles"
	},
	{
		name: "OutcomeTile",
		path: "layout/content/tiles/OutcomeTile",
		styleguidePath: "Layout/Content/Tiles/OutcomeTile",
		importPath: "web-ui-components/lib/layout/content/tiles"
	},
	{
		name: "RecapTile",
		path: "layout/content/tiles/RecapTile",
		styleguidePath: "Layout/Content/Tiles/RecapTile",
		importPath: "web-ui-components/lib/layout/content/tiles"
	},
	{
		name: "StandoutTile",
		path: "layout/content/tiles/StandoutTile",
		styleguidePath: "Layout/Content/Tiles/StandoutTile",
		importPath: "web-ui-components/lib/layout/content/tiles"
	},
	{
		name: "ValidationMessageTile",
		path: "layout/content/tiles/ValidationMessageTile",
		styleguidePath: "Layout/Content/Tiles/ValidationMessageTile",
		importPath: "web-ui-components/lib/layout/content/tiles"
	},
	{
		name: "GridListToggle",
		path: "layout/content/views/GridListToggle",
		styleguidePath: "Layout/Content/Views/GridListToggle",
		importPath: "web-ui-components/lib/layout/content/views"
	},
	{
		name: "GridViewToggle",
		path: "layout/content/views/GridViewToggle",
		styleguidePath: "Layout/Content/Views/GridViewToggle",
		importPath: "web-ui-components/lib/layout/content/views"
	},
	{
		name: "GenericFieldWrapper",
		path: "layout/fieldWrappers/GenericFieldWrapper",
		styleguidePath: "Layout/Field-wrappers/GenericFieldWrapper",
		importPath: "web-ui-components/lib/layout/fieldWrappers"
	},
	{
		name: "GroupFieldWrapper",
		path: "layout/fieldWrappers/GroupFieldWrapper",
		styleguidePath: "Layout/Field-wrappers/GroupFieldWrapper",
		importPath: "web-ui-components/lib/layout/fieldWrappers"
	},
	{
		name: "MainFixedFooter",
		path: "layout/footers/MainFixedFooter",
		styleguidePath: "Layout/Footers/MainFixedFooter",
		importPath: "web-ui-components/lib/layout/footers"
	},
	{
		name: "Grid",
		path: "layout/grids/Grid",
		styleguidePath: "Layout/Grids/Grid",
		importPath: "web-ui-components/lib/layout/grids"
	},
	{
		name: "Inline",
		path: "layout/grids/Inline",
		styleguidePath: "Layout/Grids/Inline",
		importPath: "web-ui-components/lib/layout/grids"
	},
	{
		name: "ContainedHeader",
		path: "layout/headers/ContainedHeader",
		styleguidePath: "Layout/Headers/ContainedHeader",
		importPath: "web-ui-components/lib/layout/headers"
	},
	{
		name: "FixedFooter",
		path: "layout/sections/footers/FixedFooter",
		styleguidePath: "Layout/Sections/Footers/FixedFooter",
		importPath: "web-ui-components/lib/layout/sections/footers"
	},
	{
		name: "ApplicationFormShell",
		path: "layout/shells/ApplicationFormShell",
		styleguidePath: "Layout/Shells/ApplicationFormShell",
		importPath: "web-ui-components/lib/layout/shells"
	},
	{
		name: "DataManagementShell",
		path: "layout/shells/DataManagementShell",
		styleguidePath: "Layout/Shells/DataManagementShell",
		importPath: "web-ui-components/lib/layout/shells"
	},
	{
		name: "ShellInnerSpacer",
		path: "layout/shells/ShellInnerSpacer",
		styleguidePath: "Layout/Shells/ShellInnerSpacer",
		importPath: "web-ui-components/lib/layout/shells"
	},
	{
		name: "VerticalReviewTable",
		path: "layout/tables/VerticalReviewTable",
		styleguidePath: "Layout/Tables/VerticalReviewTable",
		importPath: "web-ui-components/lib/layout/tables"
	},
	{
		name: "NonUKAddressFieldset",
		path: "location/addresses/fieldsets/NonUKAddressFieldset",
		styleguidePath: "Location/Addresses/Fieldsets/NonUKAddressFieldset",
		importPath: "web-ui-components/lib/location/addresses/fieldsets"
	},
	{
		name: "UKAddressFieldset",
		path: "location/addresses/fieldsets/UKAddressFieldset",
		styleguidePath: "Location/Addresses/Fieldsets/UKAddressFieldset",
		importPath: "web-ui-components/lib/location/addresses/fieldsets"
	},
	{
		name: "UKAddressCaptureModule",
		path: "location/addresses/modules/UKAddressCaptureModule",
		styleguidePath: "Location/Addresses/Modules/UKAddressCaptureModule",
		importPath: "web-ui-components/lib/location/addresses/modules"
	},
	{
		name: "UKAddressCaptureModule2",
		path: "location/addresses/modules/UKAddressCaptureModule2",
		styleguidePath: "Location/Addresses/Modules/UKAddressCaptureModule2",
		importPath: "web-ui-components/lib/location/addresses/modules"
	},
	{
		name: "BaseSelect",
		path: "location/places/selects/BaseSelect",
		styleguidePath: "Location/Places/Selects/BaseSelect",
		importPath: "web-ui-components/lib/location/places/selects"
	},
	{
		name: "CountrySelect",
		path: "location/places/selects/CountrySelect",
		styleguidePath: "Location/Places/Selects/CountrySelect",
		importPath: "web-ui-components/lib/location/places/selects"
	},
	{
		name: "UKCitySelect",
		path: "location/places/selects/UKCitySelect",
		styleguidePath: "Location/Places/Selects/UKCitySelect",
		importPath: "web-ui-components/lib/location/places/selects"
	},
	{
		name: "AccordionItem",
		path: "molecules/accordions/Accordion/AccordionItem",
		styleguidePath: "Molecules/Accordions/Accordion/AccordionItem",
		importPath: "web-ui-components/lib/molecules/accordions/accordion"
	},
	{
		name: "Accordion",
		path: "molecules/accordions/Accordion",
		styleguidePath: "Molecules/Accordions/Accordion",
		importPath: "web-ui-components/lib/molecules/accordions"
	},
	{
		name: "AccordionTable",
		path: "molecules/accordions/AccordionTable",
		styleguidePath: "Molecules/Accordions/AccordionTable",
		importPath: "web-ui-components/lib/molecules/accordions"
	},
	{
		name: "ContentReveal",
		path: "molecules/accordions/ContentReveal",
		styleguidePath: "Molecules/Accordions/ContentReveal",
		importPath: "web-ui-components/lib/molecules/accordions"
	},
	{
		name: "ExpansionPanel",
		path: "molecules/accordions/ExpansionPanel",
		styleguidePath: "Molecules/Accordions/ExpansionPanel",
		importPath: "web-ui-components/lib/molecules/accordions"
	},
	{
		name: "ExpansionTable",
		path: "molecules/accordions/ExpansionTable",
		styleguidePath: "Molecules/Accordions/ExpansionTable",
		importPath: "web-ui-components/lib/molecules/accordions"
	},
	{
		name: "ButtonGroup",
		path: "molecules/buttons/ButtonGroup",
		styleguidePath: "Molecules/Buttons/ButtonGroup",
		importPath: "web-ui-components/lib/molecules/buttons"
	},
	{
		name: "SortingCardGroup",
		path: "molecules/card-groups/SortingCardGroup",
		styleguidePath: "Molecules/Card-groups/SortingCardGroup",
		importPath: "web-ui-components/lib/molecules/card-groups"
	},
	{
		name: "AccountCard",
		path: "molecules/cards/AccountCard",
		styleguidePath: "Molecules/Cards/AccountCard",
		importPath: "web-ui-components/lib/molecules/cards"
	},
	{
		name: "AccountSummaryLinkCard",
		path: "molecules/cards/AccountSummaryLinkCard",
		styleguidePath: "Molecules/Cards/AccountSummaryLinkCard",
		importPath: "web-ui-components/lib/molecules/cards"
	},
	{
		name: "DateCard",
		path: "molecules/cards/DateCard",
		styleguidePath: "Molecules/Cards/DateCard",
		importPath: "web-ui-components/lib/molecules/cards"
	},
	{
		name: "PromoCard",
		path: "molecules/cards/PromoCard",
		styleguidePath: "Molecules/Cards/PromoCard",
		importPath: "web-ui-components/lib/molecules/cards"
	},
	{
		name: "StackingCard",
		path: "molecules/cards/StackingCard",
		styleguidePath: "Molecules/Cards/StackingCard",
		importPath: "web-ui-components/lib/molecules/cards"
	},
	{
		name: "Autocomplete",
		path: "molecules/forms/Autocomplete",
		styleguidePath: "Molecules/Forms/Autocomplete",
		importPath: "web-ui-components/lib/molecules/forms"
	},
	{
		name: "ConfirmationCheckbox",
		path: "molecules/forms/ConfirmationCheckbox",
		styleguidePath: "Molecules/Forms/ConfirmationCheckbox",
		importPath: "web-ui-components/lib/molecules/forms"
	},
	{
		name: "DaySelector",
		path: "molecules/forms/DaySelector",
		styleguidePath: "Molecules/Forms/DaySelector",
		importPath: "web-ui-components/lib/molecules/forms"
	},
	{
		name: "FieldBody",
		path: "molecules/forms/Field/FieldBody",
		styleguidePath: "Molecules/Forms/Field/FieldBody",
		importPath: "web-ui-components/lib/molecules/forms/field"
	},
	{
		name: "FieldHeader",
		path: "molecules/forms/Field/FieldHeader",
		styleguidePath: "Molecules/Forms/Field/FieldHeader",
		importPath: "web-ui-components/lib/molecules/forms/field"
	},
	{
		name: "FormField",
		path: "molecules/forms/Field/FormField",
		styleguidePath: "Molecules/Forms/Field/FormField",
		importPath: "web-ui-components/lib/molecules/forms/field"
	},
	{
		name: "Field",
		path: "molecules/forms/Field",
		styleguidePath: "Molecules/Forms/Field",
		importPath: "web-ui-components/lib/molecules/forms"
	},
	{
		name: "PasswordReveal",
		path: "molecules/forms/PasswordReveal",
		styleguidePath: "Molecules/Forms/PasswordReveal",
		importPath: "web-ui-components/lib/molecules/forms"
	},
	{
		name: "RadioCardGroup",
		path: "molecules/forms/RadioCardGroup",
		styleguidePath: "Molecules/Forms/RadioCardGroup",
		importPath: "web-ui-components/lib/molecules/forms"
	},
	{
		name: "Search",
		path: "molecules/forms/Search",
		styleguidePath: "Molecules/Forms/Search",
		importPath: "web-ui-components/lib/molecules/forms"
	},
	{
		name: "SearchForm",
		path: "molecules/forms/SearchForm",
		styleguidePath: "Molecules/Forms/SearchForm",
		importPath: "web-ui-components/lib/molecules/forms"
	},
	{
		name: "SegmentedControl",
		path: "molecules/forms/SegmentedControl",
		styleguidePath: "Molecules/Forms/SegmentedControl",
		importPath: "web-ui-components/lib/molecules/forms"
	},
	{
		name: "SegmentedControlItem",
		path: "molecules/forms/SegmentedControl/SegmentedControlItem",
		styleguidePath: "Molecules/Forms/SegmentedControl/SegmentedControlItem",
		importPath: "web-ui-components/lib/molecules/forms/segmentedControl"
	},
	{
		name: "SegmentedSlider",
		path: "molecules/forms/SegmentedSlider",
		styleguidePath: "Molecules/Forms/SegmentedSlider",
		importPath: "web-ui-components/lib/molecules/forms"
	},
	{
		name: "ValidationMessage",
		path: "molecules/forms/ValidationMessage",
		styleguidePath: "Molecules/Forms/ValidationMessage",
		importPath: "web-ui-components/lib/molecules/forms"
	},
	{
		name: "IconTextDynamic",
		path: "molecules/icon-text-dynamic/IconTextDynamic",
		styleguidePath: "Molecules/IconTextDynamic/IconTextDynamic",
		importPath: "web-ui-components/lib/molecules/icon-text-dynamic"
	},
	{
		name: "IconText",
		path: "molecules/icon-text/IconText",
		styleguidePath: "Molecules/IconText/IconText",
		importPath: "web-ui-components/lib/molecules/icon-text"
	},
	{
		name: "BackAnchor",
		path: "molecules/navigation/BackAnchor",
		styleguidePath: "Molecules/Navigation/BackAnchor",
		importPath: "web-ui-components/lib/molecules/navigation"
	},
	{
		name: "BackButton",
		path: "molecules/navigation/BackButton",
		styleguidePath: "Molecules/Navigation/BackButton",
		importPath: "web-ui-components/lib/molecules/navigation"
	},
	{
		name: "Utility",
		path: "molecules/navigation/Utility",
		styleguidePath: "Molecules/Navigation/Utility",
		importPath: "web-ui-components/lib/molecules/navigation"
	},
	{
		name: "Alert",
		path: "molecules/nbas/Alert",
		styleguidePath: "Molecules/NBAS/Alert",
		importPath: "web-ui-components/lib/molecules/nbas"
	},
	{
		name: "Promo",
		path: "molecules/nbas/Promo",
		styleguidePath: "Molecules/NBAS/Promo",
		importPath: "web-ui-components/lib/molecules/nbas"
	},
	{
		name: "ContrastingFormPanel",
		path: "molecules/panels/ContrastingFormPanel",
		styleguidePath: "Molecules/Panels/ContrastingFormPanel",
		importPath: "web-ui-components/lib/molecules/panels"
	},
	{
		name: "DocumentAction",
		path: "molecules/panels/DocumentAction",
		styleguidePath: "Molecules/Panels/DocumentAction",
		importPath: "web-ui-components/lib/molecules/panels"
	},
	{
		name: "IconifiedBulletPoint",
		path: "molecules/panels/IconifiedBulletPoint",
		styleguidePath: "Molecules/Panels/IconifiedBulletPoint",
		importPath: "web-ui-components/lib/molecules/panels"
	},
	{
		name: "Panel",
		path: "molecules/panels/Panel",
		styleguidePath: "Molecules/Panels/Panel",
		importPath: "web-ui-components/lib/molecules/panels"
	},
	{
		name: "PanelBody",
		path: "molecules/panels/Panel/PanelBody",
		styleguidePath: "Molecules/Panels/Panel/PanelBody",
		importPath: "web-ui-components/lib/molecules/panels/panel"
	},
	{
		name: "PanelFooter",
		path: "molecules/panels/Panel/PanelFooter",
		styleguidePath: "Molecules/Panels/Panel/PanelFooter",
		importPath: "web-ui-components/lib/molecules/panels/panel"
	},
	{
		name: "PanelHeader",
		path: "molecules/panels/Panel/PanelHeader",
		styleguidePath: "Molecules/Panels/Panel/PanelHeader",
		importPath: "web-ui-components/lib/molecules/panels/panel"
	},
	{
		name: "PanelSector",
		path: "molecules/panels/Panel/PanelSector",
		styleguidePath: "Molecules/Panels/Panel/PanelSector",
		importPath: "web-ui-components/lib/molecules/panels/panel"
	},
	{
		name: "SettingLink",
		path: "molecules/panels/SettingLink",
		styleguidePath: "Molecules/Panels/SettingLink",
		importPath: "web-ui-components/lib/molecules/panels"
	},
	{
		name: "SubordinatePanel",
		path: "molecules/panels/SubordinatePanel",
		styleguidePath: "Molecules/Panels/SubordinatePanel",
		importPath: "web-ui-components/lib/molecules/panels"
	},
	{
		name: "TogglePanel",
		path: "molecules/panels/TogglePanel",
		styleguidePath: "Molecules/Panels/TogglePanel",
		importPath: "web-ui-components/lib/molecules/panels"
	},
	{
		name: "PayeeCard",
		path: "molecules/payee-cards/PayeeCard",
		styleguidePath: "Molecules/PayeeCard/PayeeCard",
		importPath: "web-ui-components/lib/molecules/payee-cards"
	},
	{
		name: "PaymentReview",
		path: "molecules/payments/PaymentReview",
		styleguidePath: "Molecules/Payments/PaymentReview",
		importPath: "web-ui-components/lib/molecules/payments"
	},
	{
		name: "ProgressIndicator",
		path: "molecules/progress/ProgressIndicator",
		styleguidePath: "Molecules/Progress/ProgressIndicator",
		importPath: "web-ui-components/lib/molecules/progress"
	},
	{
		name: "ProgressIndicatorItem",
		path: "molecules/progress/ProgressIndicator/ProgressIndicatorItem",
		styleguidePath: "Molecules/Progress/ProgressIndicator/ProgressIndicatorItem",
		importPath: "web-ui-components/lib/molecules/progress/progressIndicator"
	},
	{
		name: "ComparisonTable",
		path: "molecules/tables/ComparisonTable",
		styleguidePath: "Molecules/Tables/ComparisonTable",
		importPath: "web-ui-components/lib/molecules/tables"
	},
	{
		name: "DataTable",
		path: "molecules/tables/Table/DataTable",
		styleguidePath: "Molecules/Tables/Table/DataTable",
		importPath: "web-ui-components/lib/molecules/tables/table"
	},
	{
		name: "Table",
		path: "molecules/tables/Table/Table",
		styleguidePath: "Molecules/Tables/Table/Table",
		importPath: "web-ui-components/lib/molecules/tables/table"
	},
	{
		name: "TableBody",
		path: "molecules/tables/Table/TableBody",
		styleguidePath: "Molecules/Tables/Table/TableBody",
		importPath: "web-ui-components/lib/molecules/tables/table"
	},
	{
		name: "TableBodyData",
		path: "molecules/tables/Table/TableBodyData",
		styleguidePath: "Molecules/Tables/Table/TableBodyData",
		importPath: "web-ui-components/lib/molecules/tables/table"
	},
	{
		name: "TableHead",
		path: "molecules/tables/Table/TableHead",
		styleguidePath: "Molecules/Tables/Table/TableHead",
		importPath: "web-ui-components/lib/molecules/tables/table"
	},
	{
		name: "TableHeadData",
		path: "molecules/tables/Table/TableHeadData",
		styleguidePath: "Molecules/Tables/Table/TableHeadData",
		importPath: "web-ui-components/lib/molecules/tables/table"
	},
	{
		name: "TableRow",
		path: "molecules/tables/Table/TableRow",
		styleguidePath: "Molecules/Tables/Table/TableRow",
		importPath: "web-ui-components/lib/molecules/tables/table"
	},
	{
		name: "SectionHeading",
		path: "molecules/text/SectionHeading",
		styleguidePath: "Molecules/Text/SectionHeading",
		importPath: "web-ui-components/lib/molecules/text"
	},
	{
		name: "ConfirmationTile",
		path: "molecules/tile/ConfirmationTile",
		styleguidePath: "Molecules/Tile/ConfirmationTile",
		importPath: "web-ui-components/lib/molecules/tile"
	},
	{
		name: "ExclamationTile",
		path: "molecules/tile/ExclamationTile",
		styleguidePath: "Molecules/Tile/ExclamationTile",
		importPath: "web-ui-components/lib/molecules/tile"
	},
	{
		name: "HighlightTile",
		path: "molecules/tile/HighlightTile",
		styleguidePath: "Molecules/Tile/HighlightTile",
		importPath: "web-ui-components/lib/molecules/tile"
	},
	{
		name: "IconTile",
		path: "molecules/tile/IconTile",
		styleguidePath: "Molecules/Tile/IconTile",
		importPath: "web-ui-components/lib/molecules/tile"
	},
	{
		name: "NumberedTile",
		path: "molecules/tile/NumberedTile",
		styleguidePath: "Molecules/Tile/NumberedTile",
		importPath: "web-ui-components/lib/molecules/tile"
	},
	{
		name: "Tile",
		path: "molecules/tile/Tile",
		styleguidePath: "Molecules/Tile/Tile",
		importPath: "web-ui-components/lib/molecules/tile"
	},
	{
		name: "TileSector",
		path: "molecules/tile/Tile/TileSector",
		styleguidePath: "Molecules/Tile/Tile/TileSector",
		importPath: "web-ui-components/lib/molecules/tile/tile"
	},
	{
		name: "TileSegment",
		path: "molecules/tile/Tile/TileSegment",
		styleguidePath: "Molecules/Tile/Tile/TileSegment",
		importPath: "web-ui-components/lib/molecules/tile/tile"
	},
	{
		name: "ToggleOption",
		path: "molecules/toggle/ToggleOption",
		styleguidePath: "Molecules/Toggle/ToggleOption",
		importPath: "web-ui-components/lib/molecules/toggle"
	},
	{
		name: "PromoAnchor",
		path: "navigation/anchors/PromoAnchor",
		styleguidePath: "Navigation/Anchors/PromoAnchor",
		importPath: "web-ui-components/lib/navigation/anchors"
	},
	{
		name: "NavigationButton",
		path: "navigation/buttons/NavigationButton",
		styleguidePath: "Navigation/Buttons/NavigationButton",
		importPath: "web-ui-components/lib/navigation/buttons"
	},
	{
		name: "NavigationButtonGroup",
		path: "navigation/buttons/NavigationButtonGroup",
		styleguidePath: "Navigation/Buttons/NavigationButtonGroup",
		importPath: "web-ui-components/lib/navigation/buttons"
	},
	{
		name: "PromoButton",
		path: "navigation/buttons/PromoButton",
		styleguidePath: "Navigation/Buttons/PromoButton",
		importPath: "web-ui-components/lib/navigation/buttons"
	},
	{
		name: "ProgressStepper",
		path: "navigation/indicators/ProgressStepper",
		styleguidePath: "Navigation/Indicators/ProgressStepper",
		importPath: "web-ui-components/lib/navigation/indicators"
	},
	{
		name: "VerticalMenu",
		path: "navigation/menus/VerticalMenu",
		styleguidePath: "Navigation/Menus/VerticalMenu",
		importPath: "web-ui-components/lib/navigation/menus"
	},
	{
		name: "MainNavLink",
		path: "navigation/nav-links/MainNavLink",
		styleguidePath: "Navigation/Nav-links/MainNavLink",
		importPath: "web-ui-components/lib/navigation/nav-links"
	},
	{
		name: "Paginator",
		path: "navigation/pagination/Paginator",
		styleguidePath: "Navigation/Pagination/Paginator",
		importPath: "web-ui-components/lib/navigation/pagination"
	},
	{
		name: "PaginatorNextButton",
		path: "navigation/pagination/Paginator/PaginatorNextButton",
		styleguidePath: "Navigation/Pagination/Paginator/PaginatorNextButton",
		importPath: "web-ui-components/lib/navigation/pagination/paginator"
	},
	{
		name: "PaginatorPageButton",
		path: "navigation/pagination/Paginator/PaginatorPageButton",
		styleguidePath: "Navigation/Pagination/Paginator/PaginatorPageButton",
		importPath: "web-ui-components/lib/navigation/pagination/paginator"
	},
	{
		name: "PaginatorPreviousButton",
		path: "navigation/pagination/Paginator/PaginatorPreviousButton",
		styleguidePath: "Navigation/Pagination/Paginator/PaginatorPreviousButton",
		importPath: "web-ui-components/lib/navigation/pagination/paginator"
	},
	{
		name: "MainSiteNav",
		path: "navigation/site-navs/MainSiteNav",
		styleguidePath: "Navigation/Site-navs/MainSiteNav",
		importPath: "web-ui-components/lib/navigation/site-navs"
	},
	{
		name: "MobileMainSiteNav",
		path: "navigation/site-navs/MobileMainSiteNav",
		styleguidePath: "Navigation/Site-navs/MobileMainSiteNav",
		importPath: "web-ui-components/lib/navigation/site-navs"
	},
	{
		name: "UtilityNav",
		path: "navigation/site-navs/UtilityNav",
		styleguidePath: "Navigation/Site-navs/UtilityNav",
		importPath: "web-ui-components/lib/navigation/site-navs"
	},
	{
		name: "UtilityNavButton",
		path: "navigation/site-navs/UtilityNavButton",
		styleguidePath: "Navigation/Site-navs/UtilityNavButton",
		importPath: "web-ui-components/lib/navigation/site-navs"
	},
	{
		name: "TabCard",
		path: "navigation/tab-cards/TabCard",
		styleguidePath: "Navigation/Tab-cards/TabCard",
		importPath: "web-ui-components/lib/navigation/tab-cards"
	},
	{
		name: "TabCardBlock",
		path: "navigation/tab-cards/TabCardBlock",
		styleguidePath: "Navigation/Tab-cards/TabCardBlock",
		importPath: "web-ui-components/lib/navigation/tab-cards"
	},
	{
		name: "TabCardSmall",
		path: "navigation/tab-cards/TabCardSmall",
		styleguidePath: "Navigation/Tab-cards/TabCardSmall",
		importPath: "web-ui-components/lib/navigation/tab-cards"
	},
	{
		name: "TabGroup",
		path: "navigation/tab-group/TabGroup",
		styleguidePath: "Navigation/Tab-group/TabGroup",
		importPath: "web-ui-components/lib/navigation/tab-group"
	},
	{
		name: "TransferTakeover",
		path: "navigation/takeovers/TransferTakeover",
		styleguidePath: "Navigation/Takeovers/TransferTakeover",
		importPath: "web-ui-components/lib/navigation/takeovers"
	},
	{
		name: "Card",
		path: "organisms/cards/Card",
		styleguidePath: "Organisms/Cards/Card",
		importPath: "web-ui-components/lib/organisms/cards"
	},
	{
		name: "DistinctiveLinkCard",
		path: "organisms/cards/DistinctiveLinkCard",
		styleguidePath: "Organisms/Cards/DistinctiveLinkCard",
		importPath: "web-ui-components/lib/organisms/cards"
	},
	{
		name: "FilterAction",
		path: "organisms/filters/Filter/FilterActionGroup/FilterAction",
		styleguidePath: "Organisms/Filters/Filter/FilterActionGroup/FilterAction",
		importPath: "web-ui-components/lib/organisms/filters/filter/filterActionGroup"
	},
	{
		name: "FilterActionGroup",
		path: "organisms/filters/Filter/FilterActionGroup",
		styleguidePath: "Organisms/Filters/Filter/FilterActionGroup",
		importPath: "web-ui-components/lib/organisms/filters/filter"
	},
	{
		name: "FilterSector",
		path: "organisms/filters/Filter/FilterSector",
		styleguidePath: "Organisms/Filters/Filter/FilterSector",
		importPath: "web-ui-components/lib/organisms/filters/filter"
	},
	{
		name: "FilterSegment",
		path: "organisms/filters/Filter/FilterSegmentGroup/FilterSegment",
		styleguidePath: "Organisms/Filters/Filter/FilterSegmentGroup/FilterSegment",
		importPath: "web-ui-components/lib/organisms/filters/filter/filterSegmentGroup"
	},
	{
		name: "FilterSegmentGroup",
		path: "organisms/filters/Filter/FilterSegmentGroup",
		styleguidePath: "Organisms/Filters/Filter/FilterSegmentGroup",
		importPath: "web-ui-components/lib/organisms/filters/filter"
	},
	{
		name: "FilterSubSegment",
		path: "organisms/filters/Filter/FilterSubSegmentGroup/FilterSubSegment",
		styleguidePath: "Organisms/Filters/Filter/FilterSubSegmentGroup/FilterSubSegment",
		importPath: "web-ui-components/lib/organisms/filters/filter/filterSubSegmentGroup"
	},
	{
		name: "FilterSubSegmentGroup",
		path: "organisms/filters/Filter/FilterSubSegmentGroup",
		styleguidePath: "Organisms/Filters/Filter/FilterSubSegmentGroup",
		importPath: "web-ui-components/lib/organisms/filters/filter"
	},
	{
		name: "Filter",
		path: "organisms/filters/Filter",
		styleguidePath: "Organisms/Filters/Filter",
		importPath: "web-ui-components/lib/organisms/filters"
	},
	{
		name: "GlobalHeader",
		path: "organisms/global/GlobalHeader",
		styleguidePath: "Organisms/Global/GlobalHeader",
		importPath: "web-ui-components/lib/organisms/global"
	},
	{
		name: "HeroHeader",
		path: "organisms/global/HeroHeader",
		styleguidePath: "Organisms/Global/HeroHeader",
		importPath: "web-ui-components/lib/organisms/global"
	},
	{
		name: "CentredModal",
		path: "organisms/modals/CentredModal",
		styleguidePath: "Organisms/Modals/CentredModal",
		importPath: "web-ui-components/lib/organisms/modals"
	},
	{
		name: "ConfirmationModal",
		path: "organisms/modals/ConfirmationModal",
		styleguidePath: "Organisms/Modals/ConfirmationModal",
		importPath: "web-ui-components/lib/organisms/modals"
	},
	{
		name: "Loader",
		path: "organisms/modals/Loader",
		styleguidePath: "Organisms/Modals/Loader",
		importPath: "web-ui-components/lib/organisms/modals"
	},
	{
		name: "ModalBody",
		path: "organisms/modals/Modal/ModalBody",
		styleguidePath: "Organisms/Modals/Modal/ModalBody",
		importPath: "web-ui-components/lib/organisms/modals/modal"
	},
	{
		name: "ModalContainer",
		path: "organisms/modals/Modal/ModalContainer",
		styleguidePath: "Organisms/Modals/Modal/ModalContainer",
		importPath: "web-ui-components/lib/organisms/modals/modal"
	},
	{
		name: "ModalHeader",
		path: "organisms/modals/Modal/ModalHeader",
		styleguidePath: "Organisms/Modals/Modal/ModalHeader",
		importPath: "web-ui-components/lib/organisms/modals/modal"
	},
	{
		name: "Modal",
		path: "organisms/modals/Modal",
		styleguidePath: "Organisms/Modals/Modal",
		importPath: "web-ui-components/lib/organisms/modals"
	},
	{
		name: "UnfinishedWorkModal",
		path: "organisms/modals/UnfinishedWorkModal",
		styleguidePath: "Organisms/Modals/UnfinishedWorkModal",
		importPath: "web-ui-components/lib/organisms/modals"
	},
	{
		name: "DisableTakeover",
		path: "organisms/takeovers/DisableTakeover",
		styleguidePath: "Organisms/Takeovers/DisableTakeover",
		importPath: "web-ui-components/lib/organisms/takeovers"
	},
	{
		name: "LoadingLocalTakeover",
		path: "organisms/takeovers/LoadingLocalTakeover",
		styleguidePath: "Organisms/Takeovers/LoadingLocalTakeover",
		importPath: "web-ui-components/lib/organisms/takeovers"
	},
	{
		name: "Timeline",
		path: "organisms/timelines/Timeline",
		styleguidePath: "Organisms/Timeline/Timeline",
		importPath: "web-ui-components/lib/organisms/timelines"
	},
	{
		name: "ProductSelector",
		path: "products/selection/ProductSelector",
		styleguidePath: "Products/Selection/ProductSelector",
		importPath: "web-ui-components/lib/products/selection"
	},
	{
		name: "EmailCreationInput",
		path: "security/authentication/inputs/EmailCreationInput",
		styleguidePath: "Security/Authentication/Inputs/EmailCreationInput",
		importPath: "web-ui-components/lib/security/authentication/inputs"
	},
	{
		name: "PasswordCreationInput",
		path: "security/authentication/inputs/PasswordCreationInput",
		styleguidePath: "Security/Authentication/Inputs/PasswordCreationInput",
		importPath: "web-ui-components/lib/security/authentication/inputs"
	},
	{
		name: "CallButton",
		path: "telephony/Buttons/CallButton",
		styleguidePath: "Telephony/Buttons/CallButton",
		importPath: "web-ui-components/lib/telephony/buttons"
	},
	{
		name: "CallStatus",
		path: "telephony/Statuses/CallStatus",
		styleguidePath: "Telephony/Statuses/CallStatus",
		importPath: "web-ui-components/lib/telephony/statuses"
	},
	{
		name: "LogoDisplay",
		path: "transactions/logo/LogoDisplay",
		styleguidePath: "Transactions/Logo/LogoDisplay",
		importPath: "web-ui-components/lib/transactions/logo"
	},
	{
		name: "Footer",
		path: "typography/ancillary/Footer",
		styleguidePath: "Typography/Ancillary/Footer",
		importPath: "web-ui-components/lib/typography/ancillary"
	},
	{
		name: "LargeBody",
		path: "typography/body/LargeBody",
		styleguidePath: "Typography/Body/LargeBody",
		importPath: "web-ui-components/lib/typography/body"
	},
	{
		name: "SecondaryBody",
		path: "typography/body/SecondaryBody",
		styleguidePath: "Typography/Body/SecondaryBody",
		importPath: "web-ui-components/lib/typography/body"
	},
	{
		name: "SignificantBody",
		path: "typography/body/SignificantBody",
		styleguidePath: "Typography/Body/SignificantBody",
		importPath: "web-ui-components/lib/typography/body"
	},
	{
		name: "SmallestBody",
		path: "typography/body/SmallestBody",
		styleguidePath: "Typography/Body/SmallestBody",
		importPath: "web-ui-components/lib/typography/body"
	},
	{
		name: "StandardBody",
		path: "typography/body/StandardBody",
		styleguidePath: "Typography/Body/StandardBody",
		importPath: "web-ui-components/lib/typography/body"
	},
	{
		name: "SubBody",
		path: "typography/body/SubBody",
		styleguidePath: "Typography/Body/SubBody",
		importPath: "web-ui-components/lib/typography/body"
	},
	{
		name: "VisuallyHiddenBody",
		path: "typography/body/VisuallyHiddenBody",
		styleguidePath: "Typography/Body/VisuallyHiddenBody",
		importPath: "web-ui-components/lib/typography/body"
	},
	{
		name: "PageHeading",
		path: "typography/headings/PageHeading",
		styleguidePath: "Typography/Headings/PageHeading",
		importPath: "web-ui-components/lib/typography/headings"
	},
	{
		name: "SectionHeading",
		path: "typography/headings/SectionHeading",
		styleguidePath: "Typography/Headings/SectionHeading",
		importPath: "web-ui-components/lib/typography/headings"
	},
	{
		name: "SubHeading",
		path: "typography/headings/SubHeading",
		styleguidePath: "Typography/Headings/SubHeading",
		importPath: "web-ui-components/lib/typography/headings"
	},
	{
		name: "SubsectionHeading",
		path: "typography/headings/SubsectionHeading",
		styleguidePath: "Typography/Headings/SubsectionHeading",
		importPath: "web-ui-components/lib/typography/headings"
	},
	{
		name: "LargeHero",
		path: "typography/heros/LargeHero",
		styleguidePath: "Typography/Heros/LargeHero",
		importPath: "web-ui-components/lib/typography/heros"
	},
	{
		name: "StandardHero",
		path: "typography/heros/StandardHero",
		styleguidePath: "Typography/Heros/StandardHero",
		importPath: "web-ui-components/lib/typography/heros"
	},
	{
		name: "IndicativeHighlight",
		path: "typography/highlights/IndicativeHighlight",
		styleguidePath: "Typography/Highlights/IndicativeHighlight",
		importPath: "web-ui-components/lib/typography/highlights"
	},
	{
		name: "StateIndicator",
		path: "typography/indicators/StateIndicator",
		styleguidePath: "Typography/Indicators/StateIndicator",
		importPath: "web-ui-components/lib/typography/indicators"
	},
	{
		name: "StepIndicator",
		path: "typography/indicators/StepIndicator",
		styleguidePath: "Typography/Indicators/StepIndicator",
		importPath: "web-ui-components/lib/typography/indicators"
	},
	{
		name: "FieldLabel",
		path: "typography/labels/FieldLabel",
		styleguidePath: "Typography/Labels/FieldLabel",
		importPath: "web-ui-components/lib/typography/labels"
	},
	{
		name: "ProminentLabel",
		path: "typography/labels/ProminentLabel",
		styleguidePath: "Typography/Labels/ProminentLabel",
		importPath: "web-ui-components/lib/typography/labels"
	},
	{
		name: "StandardLabel",
		path: "typography/labels/StandardLabel",
		styleguidePath: "Typography/Labels/StandardLabel",
		importPath: "web-ui-components/lib/typography/labels"
	},
	{
		name: "SubLabel",
		path: "typography/labels/SubLabel",
		styleguidePath: "Typography/Labels/SubLabel",
		importPath: "web-ui-components/lib/typography/labels"
	},
	{
		name: "BulletList",
		path: "typography/lists/BulletList",
		styleguidePath: "Typography/Lists/BulletList",
		importPath: "web-ui-components/lib/typography/lists"
	},
	{
		name: "NegativeList",
		path: "typography/lists/NegativeList",
		styleguidePath: "Typography/Lists/NegativeList",
		importPath: "web-ui-components/lib/typography/lists"
	},
	{
		name: "NumberList",
		path: "typography/lists/NumberList",
		styleguidePath: "Typography/Lists/NumberList",
		importPath: "web-ui-components/lib/typography/lists"
	},
	{
		name: "PositiveList",
		path: "typography/lists/PositiveList",
		styleguidePath: "Typography/Lists/PositiveList",
		importPath: "web-ui-components/lib/typography/lists"
	},
	{
		name: "TickList",
		path: "typography/lists/TickList",
		styleguidePath: "Typography/Lists/TickList",
		importPath: "web-ui-components/lib/typography/lists"
	},
	{
		name: "Identifier",
		path: "typography/modifiers/Identifier",
		styleguidePath: "Typography/Modifiers/Identifier",
		importPath: "web-ui-components/lib/typography/modifiers"
	},
	{
		name: "AbbreviatedTag",
		path: "typography/tags/AbbreviatedTag",
		styleguidePath: "Typography/Tags/AbbreviatedTag",
		importPath: "web-ui-components/lib/typography/tags"
	},
	{
		name: "StatusTag",
		path: "typography/tags/StatusTag",
		styleguidePath: "Typography/Tags/StatusTag",
		importPath: "web-ui-components/lib/typography/tags"
	},
	{
		name: "ValueTag",
		path: "typography/tags/ValueTag",
		styleguidePath: "Typography/Tags/ValueTag",
		importPath: "web-ui-components/lib/typography/tags"
	},
	{
		name: "MostProminentValue",
		path: "typography/values/MostProminentValue",
		styleguidePath: "Typography/Values/MostProminentValue",
		importPath: "web-ui-components/lib/typography/values"
	},
	{
		name: "ProminentValue",
		path: "typography/values/ProminentValue",
		styleguidePath: "Typography/Values/ProminentValue",
		importPath: "web-ui-components/lib/typography/values"
	},
	{
		name: "StandardValue",
		path: "typography/values/StandardValue",
		styleguidePath: "Typography/Values/StandardValue",
		importPath: "web-ui-components/lib/typography/values"
	},
	{
		name: "VeryProminentValue",
		path: "typography/values/VeryProminentValue",
		styleguidePath: "Typography/Values/VeryProminentValue",
		importPath: "web-ui-components/lib/typography/values"
	},
	{
		name: "UserStatusBandCard",
		path: "users/business-users/cards/UserStatusBandCard",
		styleguidePath: "Users/Business-users/Cards/UserStatusBandCard",
		importPath: "web-ui-components/lib/users/business-users/cards"
	}
];

var deprecated = [{
	name: "InputRadio",
	path: "src/actions/radios/InputRadio/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "GenericRadio",
			path: "Data/Capture/Radios"
		}
	}],
	deprecationVersion: "23.4.0",
	deprecationDate: "February 17 2020"
}, {
	name: "InputRadioGroup",
	path: "src/actions/radios/InputRadioGroup/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "GenericRadio",
			path: "Data/Capture/Radios"
		}
	}],
	deprecationVersion: "23.4.0",
	deprecationDate: "February 17 2020"
}, {
	name: "TallRadioCard",
	path: "src/actions/radios/TallRadioCard/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "GenericRadio",
			path: "Data/Capture/Radios"
		}
	}],
	deprecationVersion: "27.13.0",
	deprecationDate: "September 9 2021"
}, {
	name: "WideRadioCard",
	path: "src/actions/radios/WideRadioCard/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "GenericRadio",
			path: "Data/Capture/Radios"
		}
	}],
	deprecationVersion: "27.13.0",
	deprecationDate: "September 9 2021"
}, {
	name: "Anchor",
	path: "src/atoms/buttons/Anchor/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "WebsiteLink",
			path: "Actions/Links"
		}
	}],
	deprecationVersion: "25.4.0",
	deprecationDate: "May 01 2020"
}, {
	name: "Button",
	path: "src/atoms/buttons/Button/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: [{
			name: "ActionButton",
			path: "Actions/Buttons"
		}, {
			name: "NavigationButton",
			path: "Navigation/Buttons"
		}]
	}],
	deprecationVersion: "27.4.1",
	deprecationDate: "September 10 2020"
}, {
	name: "Checkbox",
	path: "src/atoms/forms/Checkbox/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "GenericCheckbox",
			path: "Data/Capture/Checkboxes"
		}
	}],
	deprecationVersion: "25.4.0",
	deprecationDate: "May 01 2020"
}, {
	name: "Datepicker",
	path: "src/atoms/forms/Datepicker/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "Calendar",
			path: "Atoms/Forms"
		}
	}],
	deprecationVersion: "7.3.0",
	deprecationDate: "August 30 2018"
}, {
	name: "LabelTitle",
	path: "src/atoms/forms/Label/LabelTitle/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "GenericFieldWrapper",
			path: "Layout/Field-wrappers"
		}
	}],
	deprecationVersion: "27.6.0",
	deprecationDate: "November 20 2020"
}, {
	name: "Label",
	path: "src/atoms/forms/Label/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "GenericFieldWrapper",
			path: "Layout/Field-wrappers"
		}
	}],
	deprecationVersion: "27.6.0",
	deprecationDate: "November 20 2020"
}, {
	name: "Radio",
	path: "src/atoms/forms/Radio/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "GenericRadio",
			path: "Data/Capture/Radios"
		}
	}],
	deprecationVersion: "23.2.0",
	deprecationDate: "February 06 2020"
}, {
	name: "RadioCard",
	path: "src/atoms/forms/RadioCard/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "GenericRadio",
			path: "Data/Capture/Radios"
		}
	}],
	deprecationVersion: "25.4.0",
	deprecationDate: "May 01 2020"
}, {
	name: "Select",
	path: "src/atoms/forms/Select/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "GenericSelect",
			path: "Data/Capture/Selects"
		}
	}],
	deprecationVersion: "24.0.0",
	deprecationDate: "February 27 2020"
}, {
	name: "Textarea",
	path: "src/atoms/forms/Textarea/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "GenericTextArea",
			path: "Data/Capture/TextAreas"
		}
	}],
	deprecationVersion: "19.0.0",
	deprecationDate: "May 03 2019"
}, {
	name: "TextAreaCharacterCount",
	path: "src/atoms/forms/TextAreaCharacterCount/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "GenericTextArea",
			path: "Data/Capture/TextAreas"
		}
	}],
	deprecationVersion: "20.0.0",
	deprecationDate: "May 15 2019"
}, {
	name: "Icon",
	path: "src/atoms/iconography/Icon/package.json",
	"component-life-cycle": "active",
	deprecations: [{
		replacements: {
			name: "ChevronLeftSmall",
			replacement: "ChevronLeftSolidSmall",
			path: "Atoms/Iconography"
		}
	}, {
		replacements: {
			name: "crossOutlineLarge",
			replacement: "CrossOutlineLarge",
			path: "Atoms/Iconography"
		}
	}, {
		replacements: {
			name: "crossOutlineSmall",
			replacement: "CrossOutlineSmall",
			path: "Atoms/Iconography"
		}
	}, {
		replacements: {
			name: "crossOutlineSmall",
			replacement: "CrossOutlineSmall",
			path: "Atoms/Iconography"
		}
	}, {
		replacements: {
			name: "CrossRoundedSolidLarge",
			replacement: "CrossSolidLarge",
			path: "Atoms/Iconography"
		}
	}, {
		replacements: {
			name: "CrossRoundedSolidSmall",
			replacement: "CrossSolidSmall",
			path: "Atoms/Iconography"
		}
	}, {
		replacements: {
			name: "CycleSolidLarge",
			replacement: "CycleLarge",
			path: "Atoms/Iconography"
		}
	}, {
		replacements: {
			name: "CycleSolidSmall",
			replacement: "CycleSmall",
			path: "Atoms/Iconography"
		}
	}]
}, {
	name: "Divider",
	path: "src/atoms/layout/Divider/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "GenericDivider",
			path: "Layout/Content/Breaks"
		}
	}],
	deprecationVersion: "23.1.0",
	deprecationDate: "January 31 2020"
}, {
	name: "BankHeader",
	path: "src/atoms/logo/BankHeader/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "ContainedHeader",
			path: "Layout/Headers"
		}
	}],
	deprecationVersion: "23.1.0",
	deprecationDate: "January 31 2020"
}, {
	name: "Header",
	path: "src/atoms/logo/Header/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "ContainedHeader",
			path: "Layout/Headers"
		}
	}],
	deprecationVersion: "23.1.0",
	deprecationDate: "January 31 2020"
}, {
	name: "BulletedList",
	path: "src/atoms/text/BulletedList/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "BulletList",
			path: "Typography/Lists"
		}
	}],
	deprecationVersion: "23.1.0",
	deprecationDate: "January 31 2020"
}, {
	name: "Callout",
	path: "src/atoms/text/Callout/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "ConditionalPanel",
			path: "Layout/Content/Panels"
		}
	}],
	deprecationVersion: "25.4.0",
	deprecationDate: "May 01 2020"
}, {
	name: "NumberedList",
	path: "src/atoms/text/NumberedList/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "NumberList",
			path: "Typography/Lists"
		}
	}],
	deprecationVersion: "24.0.0",
	deprecationDate: "February 27 2020"
}, {
	name: "TextBody",
	path: "src/atoms/text/TextBody/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "StandardBody",
			path: "Typography/Body"
		}
	}],
	deprecationVersion: "22.3.0",
	deprecationDate: "August 05 2019"
}, {
	name: "TextStyled",
	path: "src/atoms/text/TextStyled/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "Typography",
			path: "Typography"
		}
	}],
	deprecationVersion: "22.3.0",
	deprecationDate: "August 05 2019"
}, {
	name: "TheSmallPrint",
	path: "src/atoms/text/TheSmallPrint/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "SmallestBody",
			path: "Typography/Body"
		}
	}],
	deprecationVersion: "25.4.0",
	deprecationDate: "May 01 2020"
}, {
	name: "Title",
	path: "src/atoms/text/Title/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "Headings",
			path: "Typography"
		}
	}],
	deprecationVersion: "25.4.0",
	deprecationDate: "May 01 2020"
}, {
	name: "GenericTextInput",
	path: "src/data/capture/inputs/GenericTextInput/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "GenericAlphaNumericInput",
			path: "Data/Capture/Inputs"
		}
	}],
	deprecationVersion: "25.1.0",
	deprecationDate: "March 23 2020"
}, {
	name: "DocumentLink",
	path: "src/documents/navigation/links/DocumentLink/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "ActionButton",
			path: "Actions/Buttons"
		}
	}],
	deprecationVersion: "27.3.0",
	deprecationDate: "September 01 2020"
}, {
	name: "DocumentLinkPlate",
	path: "src/documents/navigation/plates/DocumentLinkPlate/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "Inline",
			path: "Layout/Grids"
		}
	}],
	deprecationVersion: "27.3.0",
	deprecationDate: "September 01 2020"
}, {
	name: "PrintButton",
	path: "src/documents/printing/buttons/PrintButton/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "ActionButton",
			path: "Actions/Buttons/ActionButton"
		}
	}],
	deprecationVersion: "25.4.0",
	deprecationDate: "May 01 2020"
}, {
	name: "FileInput",
	path: "src/documents/upload/FileInput/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "FileUploadInput",
			path: "Data/Capture/Inputs"
		}
	}],
	deprecationVersion: "27.6.0",
	deprecationDate: "November 20 2020"
}, {
	name: "AccountLegacyPanel",
	path: "src/finance/accounts/panels/AccountLegacyPanel/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "AccountBalancesSelectPanel",
			path: "Finance/Accounts/Panels"
		}
	}],
	deprecationVersion: "22.1.0",
	deprecationDate: "July 17 2019"
}, {
	name: "CenterImageContainer",
	path: "src/global/layout/CenterImageContainer/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "Inline",
			path: "Layout/Grids",
			variant: "Centered"
		}
	}],
	deprecationVersion: "27.9.0",
	deprecationDate: "March 24 2021"
}, {
	name: "Column",
	path: "src/global/layout/Column/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "Inline",
			path: "Layout/Grids"
		}
	}],
	deprecationVersion: "27.5.0",
	deprecationDate: "October 30 2020"
}, {
	name: "Container",
	path: "src/global/layout/Container/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "ContentContainer",
			path: "Layout/Sections"
		}
	}],
	deprecationVersion: "23.1.0",
	deprecationDate: "January 31 2020"
}, {
	name: "FlexColumn",
	path: "src/global/layout/FlexColumn/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "Inline",
			path: "Layout/Grids"
		}
	}],
	deprecationVersion: "27.5.0",
	deprecationDate: "October 30 2020"
}, {
	name: "FlexRow",
	path: "src/global/layout/FlexRow/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "Inline",
			path: "Layout/Grids"
		}
	}],
	deprecationVersion: "27.5.0",
	deprecationDate: "October 30 2020"
}, {
	name: "Frame",
	path: "src/global/layout/Frame/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "ApplicationFormShell",
			path: "Layout/Shells"
		}
	}],
	deprecationVersion: "27.4.0",
	deprecationDate: "September 07 2020"
}, {
	name: "LeftAndRightAlignment",
	path: "src/global/layout/LeftAndRightAlignment/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "Inline",
			path: "Layout/Grids"
		}
	}],
	deprecationVersion: "27.4.0",
	deprecationDate: "September 07 2020"
}, {
	name: "PageLayout",
	path: "src/global/layout/PageLayout/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "ApplicationFormShell",
			path: "Layout/Shells"
		}
	}],
	deprecationVersion: "27.4.0",
	deprecationDate: "September 07 2020"
}, {
	name: "HeaderSection",
	path: "src/global/layout/Pages/HeaderSection/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "ContainedHeader",
			path: "Layout/Headers"
		}
	}],
	deprecationVersion: "23.1.0",
	deprecationDate: "January 31 2020"
}, {
	name: "ServicingSection",
	path: "src/global/layout/Pages/ServicingSection/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "DataManagementShell",
			path: "Layout/Shells"
		}
	}],
	deprecationVersion: "25.4.0",
	deprecationDate: "May 01 2020"
}, {
	name: "WorkflowSection",
	path: "src/global/layout/Pages/WorkflowSection/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "ApplicationFormShell",
			path: "Layout/Shells"
		}
	}],
	deprecationVersion: "25.4.0",
	deprecationDate: "May 01 2020"
}, {
	name: "ResponsiveLayoutPair",
	path: "src/global/layout/ResponsiveLayoutPair/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "Inline",
			path: "Layout/Grids"
		}
	}],
	deprecationVersion: "27.2.2",
	deprecationDate: "July 31 2020"
}, {
	name: "Row",
	path: "src/global/layout/Row/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "Inline",
			path: "Layout/Grids"
		}
	}],
	deprecationVersion: "27.5.0",
	deprecationDate: "October 30 2020"
}, {
	name: "ContentContainer",
	path: "src/layout/containers/ContentContainer/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "ApplicationFormShell",
			path: "Layout/Shells"
		}
	}],
	deprecationVersion: "25.4.0",
	deprecationDate: "May 01 2020"
}, {
	name: "Main",
	path: "src/layout/containers/Main/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "ApplicationFormShell",
			path: "Layout/Shells/ApplicationFormShell"
		}
	}],
	deprecationVersion: "23.1.0",
	deprecationDate: "January 31 2020"
}, {
	name: "ContentRevealPanel",
	path: "src/layout/content/panels/ContentRevealPanel/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "SubformRevealPanel",
			path: "Layout/Content/Panels"
		}
	}],
	deprecationVersion: "27.7.0",
	deprecationDate: "December 16 2020"
}, {
	name: "ShellSidebarPanel",
	path: "src/layout/content/panels/ShellSidebarPanel/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "ShellPanel",
			path: "Layout/Content/Panels"
		}
	}],
	deprecationVersion: "27.9.0",
	deprecationDate: "March 24 2021"
}, {
	name: "GuidanceTile",
	path: "src/layout/content/tiles/GuidanceTile/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "StandoutTile",
			path: "Layout/Content/Tiles"
		}
	}],
	deprecationVersion: "25.4.0",
	deprecationDate: "May 01 2020"
}, {
	name: "RecapTile",
	path: "src/layout/content/tiles/RecapTile/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "StandoutTile",
			path: "Layout/Content/Tiles"
		}
	}],
	deprecationVersion: "25.4.0",
	deprecationDate: "May 01 2020"
}, {
	name: "GridViewToggle",
	path: "src/layout/content/views/GridViewToggle/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "GridListToggle",
			path: "Layout/Content/Views"
		}
	}],
	deprecationVersion: "28.3.0",
	deprecationDate: "January 21 2022"
}, {
	name: "FixedFooter",
	path: "src/layout/sections/footers/FixedFooter/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "MainFixedFooter",
			path: "Layout/Footers"
		}
	}],
	deprecationVersion: "23.1.0",
	deprecationDate: "January 31 2020"
}, {
	name: "AccordionItem",
	path: "src/molecules/accordions/Accordion/AccordionItem/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "GenericAccordion",
			path: "Layout/Content/Accordions"
		}
	}],
	deprecationVersion: "25.4.0",
	deprecationDate: "May 01 2020"
}, {
	name: "Accordion",
	path: "src/molecules/accordions/Accordion/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "GenericAccordion",
			path: "Layout/Content/Accordions"
		}
	}],
	deprecationVersion: "25.4.0",
	deprecationDate: "May 01 2020"
}, {
	name: "AccordionTable",
	path: "src/molecules/accordions/AccordionTable/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "GenericAccordion",
			path: "Layout/Content/Accordions"
		}
	}],
	deprecationVersion: "25.4.0",
	deprecationDate: "May 01 2020"
}, {
	name: "ContentReveal",
	path: "src/molecules/accordions/ContentReveal/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "ContentRevealPanel",
			path: "Layout/Content/Panels"
		}
	}],
	deprecationVersion: "24.1.0",
	deprecationDate: "March 05 2020"
}, {
	name: "ExpansionPanel",
	path: "src/molecules/accordions/ExpansionPanel/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: [{
			name: "SubformRevealPanel",
			path: "Layout/Content/Panels"
		}, {
			name: "GenericAccordion",
			path: "Layout/Content/Accordions/GenericAccordion"
		}, {
			name: "InformationRevealPanel",
			path: "Layout/Content/Panels"
		}]
	}],
	deprecationVersion: "27.3.0",
	deprecationDate: "September 01 2020"
}, {
	name: "ExpansionTable",
	path: "src/molecules/accordions/ExpansionTable/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "ContentRevealPanel",
			path: "Layout/Content/Panels"
		}
	}],
	deprecationVersion: "27.3.0",
	deprecationDate: "September 01 2020"
}, {
	name: "ButtonGroup",
	path: "src/molecules/buttons/ButtonGroup/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: [{
			name: "NavigationButtonGroup",
			path: "Navigation/Buttons"
		}, {
			name: "Inline",
			path: "Layout/Grids"
		}]
	}],
	deprecationVersion: "27.4.1",
	deprecationDate: "September 10 2020"
}, {
	name: "ConfirmationCheckbox",
	path: "src/molecules/forms/ConfirmationCheckbox/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "StandaloneCheckbox",
			path: "Data/Capture/Checkboxes"
		}
	}],
	deprecationVersion: "25.4.0",
	deprecationDate: "May 01 2020"
}, {
	name: "DaySelector",
	path: "src/molecules/forms/DaySelector/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "GenericRadio",
			path: "Data/Capture/Radios"
		}
	}],
	deprecationVersion: "25.4.0",
	deprecationDate: "May 01 2020"
}, {
	name: "FieldBody",
	path: "src/molecules/forms/Field/FieldBody/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "GenericFieldWrapper",
			path: "Layout/Field-wrappers"
		}
	}],
	deprecationVersion: "27.4.0",
	deprecationDate: "September 07 2020"
}, {
	name: "FieldHeader",
	path: "src/molecules/forms/Field/FieldHeader/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "GenericFieldWrapper",
			path: "Layout/Field-wrappers"
		}
	}],
	deprecationVersion: "27.4.0",
	deprecationDate: "September 07 2020"
}, {
	name: "Field",
	path: "src/molecules/forms/Field/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "GenericFieldWrapper",
			path: "Layout/Field-wrappers"
		}
	}],
	deprecationVersion: "27.4.0",
	deprecationDate: "September 07 2020"
}, {
	name: "RadioCardGroup",
	path: "src/molecules/forms/RadioCardGroup/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "GenericRadio",
			path: "Data/Capture/Radios"
		}
	}],
	deprecationVersion: "25.4.0",
	deprecationDate: "May 01 2020"
}, {
	name: "SegmentedSlider",
	path: "src/molecules/forms/SegmentedSlider/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "SliderInput",
			path: "Data/Capture/Inputs"
		}
	}],
	deprecationVersion: "28.4.0",
	deprecationDate: "June 21 2022"
}, {
	name: "ValidationMessage",
	path: "src/molecules/forms/ValidationMessage/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "ValidationMessageTile",
			path: "Layout/Tiles"
		}
	}],
	deprecationVersion: "22.22.0",
	deprecationDate: "January 20 2020"
}, {
	name: "IconText",
	path: "src/molecules/icon-text/IconText/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "IconTextDynamic",
			path: "Molecules/IconTextDynamic"
		}
	}],
	deprecationVersion: "6.4.0",
	deprecationDate: "August 03 2018"
}, {
	name: "BackAnchor",
	path: "src/molecules/navigation/BackAnchor/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "WebsiteLink",
			path: "Actions/Links"
		}
	}],
	deprecationVersion: "25.4.0",
	deprecationDate: "May 01 2020"
}, {
	name: "BackButton",
	path: "src/molecules/navigation/BackButton/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "UtilityNavButton",
			path: "Navigation/Site-navs"
		}
	}],
	deprecationVersion: "25.5.0",
	deprecationDate: "May 13 2020"
}, {
	name: "Utility",
	path: "src/molecules/navigation/Utility/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "UtilityNav",
			path: "Navigation/Site-Navs/UtilityNav"
		}
	}],
	deprecationVersion: "22.12.0",
	deprecationDate: "September 27 2019"
}, {
	name: "ContrastingFormPanel",
	path: "src/molecules/panels/ContrastingFormPanel/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "GenericPanel",
			path: "Layout/Content/Panels",
			variant: "Interaction"
		}
	}],
	deprecationVersion: "33.3.0",
	deprecationDate: "April 03 2024"
}, {
	name: "SubordinatePanel",
	path: "src/molecules/panels/SubordinatePanel/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "ConditionalPanel",
			path: "Layout/Content/Panels"
		}
	}],
	deprecationVersion: "23.1.0",
	deprecationDate: "January 31 2020"
}, {
	name: "ProgressIndicator",
	path: "src/molecules/progress/ProgressIndicator/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "ProgressStepper",
			path: "Navigation/Indicators"
		}
	}],
	deprecationVersion: "26.0.0",
	deprecationDate: "May 22 2020"
}, {
	name: "ProgressIndicatorItem",
	path: "src/molecules/progress/ProgressIndicator/ProgressIndicatorItem/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "ProgressStepper",
			path: "Navigation/Indicators"
		}
	}],
	deprecationVersion: "26.0.0",
	deprecationDate: "May 22 2020"
}, {
	name: "SectionHeading",
	path: "src/molecules/text/SectionHeading/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "SectionHeading",
			path: "Typography/Headings"
		}
	}],
	deprecationVersion: "23.2.0",
	deprecationDate: "February 06 2020"
}, {
	name: "ConfirmationTile",
	path: "src/molecules/tile/ConfirmationTile/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "OutcomeTile",
			path: "Layout/Content/Tiles"
		}
	}],
	deprecationVersion: "25.4.0",
	deprecationDate: "May 01 2020"
}, {
	name: "HighlightTile",
	path: "src/molecules/tile/HighlightTile/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "StandoutTile",
			path: "Layout/Content/Tiles"
		}
	}],
	deprecationVersion: "25.4.0",
	deprecationDate: "May 01 2020"
}, {
	name: "IconTile",
	path: "src/molecules/tile/IconTile/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "OutcomeTile",
			path: "Layout/Content/Tiles"
		}
	}],
	deprecationVersion: "25.4.0",
	deprecationDate: "May 01 2020"
}, {
	name: "NavigationButton",
	path: "src/navigation/buttons/NavigationButton/package.json",
	"component-life-cycle": "active",
	deprecations: [{
		variants: ["Confirm", "Continue", "Next", "Submit"],
		replacements: {
			name: "NavigationButton",
			path: "Navigation/Buttons",
			variant: "Primary"
		}
	}, {
		variants: ["Back"],
		replacements: {
			name: "NavigationButton",
			path: "Navigation/Buttons",
			variant: "Secondary"
		}
	}, {
		variants: ["Cancel"],
		replacements: {
			name: "NavigationButton",
			path: "Navigation/Buttons",
			variant: "Tertiary"
		}
	}],
	deprecationVersion: "NO RELEASE VERSION FOUND",
	deprecationDate: "NO RELEASE DATE FOUND"
}, {
	name: "GlobalHeader",
	path: "src/organisms/global/GlobalHeader/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "ContainedHeader",
			path: "Layout/Headers"
		}
	}],
	deprecationVersion: "25.4.0",
	deprecationDate: "May 01 2020"
}, {
	name: "CentredModal",
	path: "src/organisms/modals/CentredModal/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "ConfirmationModal",
			path: "Organisms/Modals"
		}
	}],
	deprecationVersion: "22.0.0",
	deprecationDate: "July 04 2019"
}, {
	name: "ConfirmationModal",
	path: "src/organisms/modals/ConfirmationModal/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "GenericModal",
			path: "Layout/Content/Modals"
		}
	}],
	deprecationVersion: "27.8.0",
	deprecationDate: "January 29 2021"
}, {
	name: "Modal",
	path: "src/organisms/modals/Modal/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "ConfirmationModal",
			path: "Organisms/Modals"
		}
	}],
	deprecationVersion: "22.0.0",
	deprecationDate: "July 04 2019"
}, {
	name: "LargeHero",
	path: "src/typography/heros/LargeHero/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "Typography",
			path: "Typography"
		}
	}],
	deprecationVersion: "24.0.0",
	deprecationDate: "February 27 2020"
}, {
	name: "StandardHero",
	path: "src/typography/heros/StandardHero/package.json",
	"component-life-cycle": "deprecated",
	deprecations: [{
		replacements: {
			name: "Typography",
			path: "Typography"
		}
	}],
	deprecationVersion: "24.0.0",
	deprecationDate: "February 27 2020"
}];

/* eslint-disable no-console */

var deprecatedDictComponents = {};
var deprecatedDictProps = {};
var timeoutDelay = 100;

var CONSOLE_DEPRECATION_MESSAGE_START = 'Deprecation: web-ui-components';

var DeprecationWarnings = {
	logComponent: function logComponent(componentName) {
		var deprecatedComponentObj = componentPaths.find(function (_ref) {
			var name = _ref.name;
			return name === componentName;
		});
		var oldPath = deprecatedComponentObj.styleguidePath;
		deprecatedComponentObj = deprecated.find(function (_ref2) {
			var name = _ref2.name;
			return name.includes(componentName);
		});

		var depreactionReplacements = deprecatedComponentObj.deprecations[0].replacements;
		var hasMultipleReplacements = Array.isArray(depreactionReplacements);
		var newPath = hasMultipleReplacements ? depreactionReplacements.map(function (replacement) {
			return replacement.path + '/' + replacement.name;
		}) : depreactionReplacements.path + '/' + depreactionReplacements.name;

		deprecatedDictComponents[componentName] = { oldPath: oldPath, newPath: newPath };
	},
	logIcon: function logIcon(componentName, deprecatedIcon) {
		var deprecatedComponentObj = componentPaths.find(function (_ref3) {
			var name = _ref3.name;
			return name.includes(componentName);
		});
		var path = deprecatedComponentObj.styleguidePath;
		var oldPath = path + ': ' + deprecatedIcon;
		var newPath = path + ': ' + deprecatedIcon;
		var iconName = componentName + ': ' + deprecatedIcon;

		deprecatedDictComponents[iconName] = { oldPath: oldPath, newPath: newPath };
	},
	logProp: function logProp(packageJson, description) {
		var _packageJson$main$spl = packageJson.main.split('.'),
		    _packageJson$main$spl2 = slicedToArray(_packageJson$main$spl, 1),
		    componentName = _packageJson$main$spl2[0];

		var deprecatedComponentObj = componentPaths.find(function (_ref4) {
			var name = _ref4.name;
			return name.includes(componentName);
		});
		var path = deprecatedComponentObj.styleguidePath;
		deprecatedDictProps[componentName] = { path: path, description: description };
	},
	report: function report() {
		setTimeout(function () {
			var numberOfDeprecations = Object.keys(deprecatedDictComponents).length + Object.keys(deprecatedDictProps).length;
			if (numberOfDeprecations > 0) {
				console.groupCollapsed('Web UI Components Deprecation Warnings. Total: ' + numberOfDeprecations);

				console.groupCollapsed('Components. Total: ' + Object.keys(deprecatedDictComponents).length);
				Object.entries(deprecatedDictComponents).forEach(function (_ref5) {
					var _ref6 = slicedToArray(_ref5, 2),
					    componentName = _ref6[0],
					    info = _ref6[1];

					var oldPath = info.oldPath,
					    newPath = info.newPath;

					console.groupCollapsed(componentName);
					console.warn('Deprecated:', oldPath);

					var hasMultipleReplacements = Array.isArray(newPath);
					if (hasMultipleReplacements) {
						newPath.map(function (replacement, index) {
							return console.warn('Replacement ' + (index + 1) + ':', replacement);
						});
					} else {
						console.warn('Replacement:', newPath);
					}
					console.groupEnd();
				});
				console.groupEnd();

				console.groupCollapsed('Props. Total: ' + Object.keys(deprecatedDictProps).length);
				Object.entries(deprecatedDictProps).forEach(function (_ref7) {
					var _ref8 = slicedToArray(_ref7, 2),
					    componentName = _ref8[0],
					    info = _ref8[1];

					var path = info.path,
					    description = info.description;

					console.groupCollapsed(componentName);
					console.warn('Deprecated:', path);
					console.warn('Reason:', description);
					console.groupEnd();
				});
				console.groupEnd();

				console.groupEnd();
				deprecatedDictComponents = {};
				deprecatedDictProps = {};
			}
		}, timeoutDelay);
	},
	warn: function warn(message) {
		return console.warn(CONSOLE_DEPRECATION_MESSAGE_START + ' -> ' + message);
	}
};

Object.freeze(DeprecationWarnings);

var main = "DateCard.js";
var packageJson = {
	main: main,
	"component-id": null,
	"component-life-cycle": "active",
	"is-bespoke": false
};

// TODOxCYBG: Once converted to Emotion, we need to update the styles in <Timeline /> to reflect

var DateCard = function DateCard(_ref) {
	var children = _ref.children,
	    className = _ref.className,
	    date = _ref.date,
	    id = _ref.id,
	    isBranded = _ref.isBranded,
	    isJoined = _ref.isJoined,
	    isPrefixed = _ref.isPrefixed,
	    isSeparated = _ref.isSeparated,
	    _onClick = _ref.onClick,
	    selected = _ref.selected,
	    title = _ref.title;

	if (typeof date === 'string' || typeof date === 'number') {
		DeprecationWarnings.logProp(packageJson, 'The string and number format for date prop will be deprecated in a future release as it is not fully supported. Please use a proper ISO formatted date, as shown in the component example.');
	}
	var classNames = classnames('c-m-date-card', className, {
		'c-m-date-card--branded': isBranded,
		'is-selected': selected
	});
	var prefixClassNames = classnames('c-m-date-card__prefix', {
		'c-m-date-card__prefix--joined': isJoined
	});
	var titleClassNames = classnames('c-m-date-card__panel', 'u-faux-block-link', {
		'u-margin-bottom-1': isSeparated
	});
	var momentDate = moment(date);
	var month = momentDate.format('MMM');
	var day = momentDate.format('DD');
	var year = momentDate.format('YYYY');
	return React.createElement(
		'section',
		{ key: id, id: id, className: classNames },
		isPrefixed && React.createElement(
			'div',
			{ className: prefixClassNames },
			React.createElement('span', { className: 'c-m-date-card__prefix-indicator' })
		),
		date && React.createElement(
			'div',
			{ className: 'c-m-date-card__calendar' },
			React.createElement(
				'span',
				{ className: 'c-m-date-card__calendar-month' },
				month
			),
			React.createElement(
				'span',
				{ className: 'c-m-date-card__calendar-day' },
				day
			),
			React.createElement(
				'span',
				{ className: 'c-m-date-card__calendar-year' },
				year
			)
		),
		React.createElement(
			'div',
			{ className: titleClassNames },
			title && React.createElement(
				'div',
				{ className: 'c-m-date-card__panel-header' },
				React.createElement(
					'h3',
					{ className: 'c-m-date-card__panel-title' },
					_onClick ? React.createElement(
						'a',
						{
							href: '#\\',
							onClick: function onClick(e) {
								e.preventDefault();
								_onClick(id);
							},
							className: 'u-faux-block-link__overlay'
						},
						title
					) : title
				)
			),
			children && React.createElement(
				'div',
				{ className: 'c-m-date-card__panel-body' },
				React.createElement(
					'div',
					{ className: 'c-m-date-card__panel-content' },
					children
				)
			)
		)
	);
};

DateCard.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
	date: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]).isRequired,
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	isBranded: PropTypes.bool,
	isJoined: PropTypes.bool,
	isPrefixed: PropTypes.bool,
	isSeparated: PropTypes.bool,
	onClick: PropTypes.func,
	selected: PropTypes.bool,
	title: PropTypes.string
};

DateCard.defaultProps = {
	selected: false
};

var PromoCard = function PromoCard(_ref) {
	var children = _ref.children,
	    className = _ref.className,
	    title = _ref.title,
	    icon = _ref.icon,
	    link = _ref.link,
	    branded = _ref.branded,
	    onLinkClick = _ref.onLinkClick;

	var promoCardClasses = classnames('c-m-promo-card', className, {
		'c-m-promo-card--branded': branded,
		'u-faux-block-link': link
	});
	return React.createElement(
		'section',
		{ className: promoCardClasses },
		React.createElement(
			'div',
			{ className: 'c-m-promo-card__body' },
			icon && React.createElement(
				'div',
				{ className: 'c-m-promo-card__icon' },
				icon
			),
			React.createElement(
				'div',
				{ className: 'c-m-promo-card__content' },
				title && React.createElement(
					'h3',
					{ className: 'c-m-promo-card__title u-type-size-arran' },
					link ? React.createElement(
						'a',
						{ href: link, onClick: onLinkClick, className: 'u-faux-block-link__overlay' },
						title
					) : title
				),
				children
			)
		)
	);
};

PromoCard.propTypes = {
	children: PropTypes.node,
	className: PropTypes.string,
	title: PropTypes.string,
	icon: PropTypes.node,
	link: PropTypes.string,
	branded: PropTypes.bool,
	onLinkClick: PropTypes.func
};

PromoCard.defaultProps = {
	branded: false
};

var wrapperClassNames = function wrapperClassNames(selected) {
	return classnames('c-m-sorting-card-group__data-table-entry', {
		'c-m-sorting-card-group__data-table-entry--selected': selected
	});
};

var StackingCard = function StackingCard(_ref) {
	var children = _ref.children,
	    selected = _ref.selected;
	return React.createElement(
		'div',
		{ className: wrapperClassNames(selected) },
		children
	);
};

StackingCard.propTypes = {
	children: PropTypes.arrayOf(PropTypes.node).isRequired,
	selected: PropTypes.bool
};

StackingCard.defaultProps = {
	selected: false
};

var wrapperClassNames$1 = function wrapperClassNames(sorted, widthProportion) {
	var _classnames;

	return classnames('c-m-sorting-card-group__data-table-row', (_classnames = {}, defineProperty(_classnames, 'c-m-sorting-card-group__data-table-row--width-' + widthProportion, widthProportion), defineProperty(_classnames, 'c-m-sorting-card-group__data-table-row--sorted', sorted), _classnames));
};

var StackingCardCell = function StackingCardCell(_ref) {
	var children = _ref.children,
	    label = _ref.label,
	    sorted = _ref.sorted,
	    value = _ref.value,
	    widthProportion = _ref.widthProportion;
	return React.createElement(
		'div',
		{ className: wrapperClassNames$1(sorted, widthProportion) },
		label && React.createElement(
			'span',
			{ className: 'c-m-sorting-card-group__data-table-label' },
			label
		),
		React.createElement(
			'div',
			{ className: 'c-m-sorting-card-group__data-table-value' },
			value && React.createElement(
				'span',
				{ className: 'c-m-sorting-card-group__data-table-value-label' },
				value
			),
			children
		)
	);
};

var VALID_WIDTH_PROPORTION_1 = 1;
var VALID_WIDTH_PROPORTION_2 = 2;
var VALID_WIDTH_PROPORTION_3 = 3;
var VALID_WIDTH_PROPORTION_4 = 4;
var VALID_WIDTH_PROPORTION_5 = 5;

StackingCardCell.propTypes = {
	children: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.element]),
	label: PropTypes.string,
	sorted: PropTypes.bool,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.element]),
	/** Width proportion dictates the column width. */
	widthProportion: PropTypes.oneOf([VALID_WIDTH_PROPORTION_1, VALID_WIDTH_PROPORTION_2, VALID_WIDTH_PROPORTION_3, VALID_WIDTH_PROPORTION_4, VALID_WIDTH_PROPORTION_5])
};

StackingCardCell.defaultProps = {};

export { WrappedAccountCard as AccountCard, WrappedAccountSummaryLinkCard as AccountSummaryLinkCard, DateCard, PromoCard, StackingCard, StackingCardCell };
